//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { flushSync } from 'react-dom';
import { Global } from '@emotion/react';
import scrollbarStyles from '../../../styles/scrollbarStyles';
import { useUserAuth } from '../../../UserAuth';
import DefaultIntegrationSelectionModal from './DefaultIntegrationSelectionModal';
import NoIntegrationAccessNotice from './NoIntegrationAccessNotice';
import IntegrationPreferencesService from '../../../utils/IntegrationPreferencesService';
import {
  DEFAULT_INTEGRATION_CHANGED_EVENT,
  IntegrationAccessIssue,
  integrationAccessIssue,
} from '../../../types/integrationPreferences';
import { cardStyles } from '../../../styles/LoopStyles';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';
import AiDataService from '../../../utils/AiDataService';
import { saveBlobAsFile } from '../../../utils/fileDownload';
import { ToolOutputDownloadContext } from './toolResults/ToolOutputDownloadContext';
import MCPDataService from '../../../utils/MCPDataService';
import { useTeamspaceContext } from '../../../TeamspaceContext';
import {
  extractErrorInfo,
  handleBackendErrorResponse,
  handleGeneralError,
} from './AIErrorHandler';
import settings from '../../../Config/settings';
import ExecutionPlan from './ExecutionPlan';
import SocketService from './SocketService';
import { ClarificationRequest } from './SocketService';
import ChatThread from './ChatThread';
import ChatHeader from './ChatHeader';
import type { TaskIcon } from './taskIcon';
import ChatInputArea from './ChatInputArea';
import { useIngestJobsPolling } from '../../Integration/useIngestJobsPolling';
import type {
  ToolApprovalRequest,
  PlanApprovalRequest,
  IterationWarningEvent,
  HitlDecision,
  ToolOutputRef,
  ToolResultBlock,
  ToolStatus,
} from '../../../types/streaming';
import { useConfig } from '../../../../src/Config';
import TasksTab from './TasksTab';
import HistoryTab from './HistoryTab';
import MCPConnectionsTab from './MCPConnectionsTab';
import { useTheme } from '../../../ThemeContext';
import { useAnnouncementOffset } from '../../../AnnouncementOffsetContext';
import { isActivationContent, stripActivationMarker } from './taskActivation';
import { isSafeKey, safeAssignKey } from '../../../utils/securityUtils';
import {
  sanitizeChatForDownload,
  TRANSCRIPT_LOG_PREFIX,
} from './transcriptExport';
import type { BackendTurn } from '../../../types/conversationHistory';
import type {
  ExecutionReceiptEvent,
  ExecutionTerminalEvent,
  ExecutionTerminalStatus,
  StoredExecutionReceipt,
} from '../../../types/executionReceipt';
import { isSuppressedBeforeMessage } from './turnGrouping';

type TaskStarter = {
  label: string;
  prompt: string;
};

// Type definitions for search results
interface SearchHit {
  issueId: string;
  issueName: string;
  issueOwner: string;
  issueCreator: string;
  issueStatus: string;
  issueType: string;
  issueCreated: string;
  issueLastUpdate: string;
  issueDescription: string;
  issueUrl: string;
  pullRequestUrls: string[];
  commitUrls: string[];
  buildUrls: string[];
}

interface SearchResults {
  hits: SearchHit[];
}

interface SearchContext {
  results: SearchResults;
  renderedResults: React.ReactNode;
  searchContext: string;
  hasSearched: boolean;
}

// Correlates a message with the turn (one user request + everything the
// agent does in response) that produced it. Optional so every message
// created before this field existed — and anything restored from history —
// stays valid; messages without it group into one legacy turn (FR-002).
type TurnStamp = { turnId?: string };

export type ChatMessage =
  | ({
      sender: 'user' | 'ai';
      message: string;
      subMessage?: React.ReactNode;
      isStreaming?: boolean;
      // Present only on the AI "divider" message created when a task is
      // selected. Does not affect rendering; it lets the download mark the
      // task context (mode, which task, description) and segment the
      // transcript when the user switches tasks within one session.
      taskMeta?: {
        taskId?: string;
        taskName: string;
        taskDescription?: string;
      };
    } & TurnStamp)
  | ({
      sender: 'tool';
      toolCallId: string;
      toolName: string;
      toolArgs: Record<string, unknown>;
      toolResult?: string;
      toolResultBlocks?: ToolResultBlock[];
      // Spec 007: set instead of toolResult when the output was too large to
      // keep as text. The row shows "Output too large (N characters)" and a
      // Download button; the text itself never reaches the page.
      toolOutputRef?: ToolOutputRef;
      toolStatus: ToolStatus;
      // Client-observed timestamps (ms since epoch), for a *live* call: set
      // on tool_call_start (preserved across an approval-replay upsert) and
      // tool_call_result respectively. Never set on a restored/historical
      // message — see reportedDurationMs below for that case.
      startedAt?: number;
      endedAt?: number;
      // The backend's own measured duration for a restored tool call
      // (analyticsai-server PR #409, ToolResultPayload.duration_ms) — never
      // set on a live message, only on one produced by
      // mapBackendTurnToChatMessages. turnGrouping.ts's toToolTraceRow
      // prefers this over the startedAt/endedAt computation when present,
      // per the "prefer a reported duration" rule ToolTraceRow already
      // documents (T022, spec 006 convergence).
      reportedDurationMs?: number;
      // Set once a call is ever gated by an approval request, and never
      // cleared afterward. Identifies a gated call; whether its card is
      // still in the column depends on gateDecision below, not on this
      // alone (S3b — pinning every decided approval forever reintroduced
      // the wall-of-cards problem for any turn with several).
      wasGated?: true;
      // Recorded in onHitlDecide as the decision is sent, because
      // toolStatus moves on to 'running'/'success'/'error' immediately
      // afterwards and no longer carries the decision. Absent while a
      // wasGated call's approval is still outstanding — that is the one
      // case whose card stays inline. A rejected call never runs, but its
      // row stays in the record marked rejected.
      gateDecision?: 'approved' | 'rejected';
    } & TurnStamp)
  | ({
      sender: 'plan_approval';
      planCallId: string;
      plan: PlanApprovalRequest;
      // 'pending' renders the live, interactive card; any other value is a
      // decided historical record — collapsed by default, no action buttons.
      // 'expired' covers abandonment paths (task switch, socket error, panel
      // close, new turn started) where the interrupt was never answered.
      decision:
        | 'pending'
        | 'approved'
        | 'rejected'
        | 'changes_requested'
        | 'expired';
    } & TurnStamp)
  | ({
      // The report produced after an approved plan finished (what changed,
      // what was skipped, what did not happen, an audit line per call).
      // Live from the execution_receipt event, restored from the stored
      // turn event of the same name.
      sender: 'execution_receipt';
      planCallId: string;
      receipt: StoredExecutionReceipt;
      terminalStatus: ExecutionTerminalStatus | null;
      // Set only when the receipt arrived over the socket during this session,
      // never on one restored from history: ChatThread announces live ones only.
      live?: boolean;
    } & TurnStamp)
  | ({
      // The end-of-run report for an approved plan's execution that has no
      // receipt covering it (some calls were refused, or no receipt could be
      // produced). Live only — the server does not store this event, so it is
      // not restored from history.
      sender: 'execution_terminal';
      event: ExecutionTerminalEvent;
      live?: boolean;
    } & TurnStamp);

type PlanApprovalMessage = Extract<ChatMessage, { sender: 'plan_approval' }>;
type ToolMessage = Extract<ChatMessage, { sender: 'tool' }>;

// A minimal shape for a plain-text history row (today's `GET /api/messages`
// response) — only the fields the legacy mapper below ever reads.
interface PlainHistoryMessage {
  role?: string;
  message?: string;
  [key: string]: unknown;
}

/**
 * Reads the size reference the service sends for an output that was too large to keep as
 * text (spec 007), from either the live event or a saved history record. Anything that is
 * not a well-formed reference gives `undefined`, so a malformed value can never hide a real
 * output.
 */
export const toToolOutputRef = (
  reference: unknown,
  conversationId?: string,
): ToolOutputRef | undefined => {
  if (!reference || typeof reference !== 'object') return undefined;
  const { chars, lines, model_view } = reference as {
    chars?: unknown;
    lines?: unknown;
    model_view?: unknown;
  };
  if (typeof chars !== 'number' || !Number.isFinite(chars) || chars < 0) {
    return undefined;
  }
  return {
    chars,
    ...(typeof lines === 'number' && Number.isFinite(lines) ? { lines } : {}),
    ...(typeof model_view === 'string' && model_view
      ? { modelView: model_view }
      : {}),
    ...(conversationId ? { conversationId } : {}),
  };
};

/**
 * Maps one backend-recorded turn (analyticsai-server PR #409's per-turn
 * event record — see specs/006-persist-history-reconstruction/data-model.md)
 * onto the `ChatMessage[]` shapes this codebase already renders for live
 * turns. Pure function — no side effects, no client-side reordering: event
 * order within the turn is preserved from the backend's own `seq` (FR-009).
 *
 * - `assistant_text` -> `sender: 'ai'`, regardless of how the turn ended
 *   (FR-002); an empty/absent text contributes no message (no invented
 *   empty bubble).
 * - `plan_request` (+ optional `plan_decision`) -> `sender: 'plan_approval'`.
 *   A decided plan maps `approved`/`rejected` directly; an undecided one
 *   maps to the existing `'expired'` state (R4) — never `'pending'`, since
 *   a restored plan is never the live, interactive card (FR-011).
 * - `tool_call` (+ optional `tool_approval_request`/`_decision` and
 *   `tool_result`) -> `sender: 'tool'`. A call gated by an approval request
 *   sets `wasGated: true`; its decision maps `approve`/`edit` -> 'approved'
 *   (an edited-then-run call did proceed, R3), `reject` -> 'rejected'. A
 *   call with no matching result maps to `toolStatus: 'error'` (R3/edge
 *   case: a restored, concluded turn never has a "still running" call).
 */
export const mapBackendTurnToChatMessages = (
  turn: BackendTurn,
): ChatMessage[] => {
  const output: ChatMessage[] = [];
  const planMessagesByCallId = new Map<string, PlanApprovalMessage>();
  const mostRecentPlanCallId: { current: string | null } = { current: null };
  const toolMessagesByCallId = new Map<string, ToolMessage>();
  // Mirrors mostRecentPlanCallId above, for the same reason:
  // ToolApprovalDecisionPayload.tool_call_id is typed optional (same as
  // PlanDecisionPayload.plan_call_id), and without a fallback here an event
  // that omits it silently drops the decision — the restored row keeps no
  // gateDecision, misrepresenting an actually-decided call as still
  // undecided (found in review of #989).
  const mostRecentGatedToolCallId: { current: string | null } = {
    current: null,
  };

  const sortedEvents = [...turn.events].sort((a, b) => a.seq - b.seq);

  for (const event of sortedEvents) {
    switch (event.event_type) {
      case 'assistant_text': {
        if (event.payload.text) {
          output.push({
            sender: 'ai',
            message: event.payload.text,
            turnId: turn.turn_id,
          });
        }
        break;
      }

      case 'plan_request': {
        const { plan_call_id, intent, referents, out_of_scope } = event.payload;
        // A repeated plan_call_id means the backend re-emitted the same
        // interrupt (observed live: two identical plan_request rows for one
        // call_id, one plan_decision) — that's one logical plan, not two.
        // Update the existing record in place rather than pushing a second
        // output entry, which otherwise strands the first as a permanent,
        // never-decided "expired" card even though the backend's one real
        // decision does get attached (by call_id) to *a* message — just not
        // to this now-orphaned first one.
        const existing = planMessagesByCallId.get(plan_call_id);
        if (existing) {
          existing.plan = {
            type: 'plan_approval_request',
            intent: intent ?? '',
            referents: referents ?? [],
            out_of_scope: out_of_scope ?? '',
          };
          mostRecentPlanCallId.current = plan_call_id;
          break;
        }
        const planMessage: PlanApprovalMessage = {
          sender: 'plan_approval',
          planCallId: plan_call_id,
          plan: {
            type: 'plan_approval_request',
            intent: intent ?? '',
            referents: referents ?? [],
            out_of_scope: out_of_scope ?? '',
          },
          // No decision seen yet — 'expired' is the correct default for a
          // plan whose conversation ended before this loop finds one
          // (R4); a following plan_decision event overwrites this below.
          decision: 'expired',
          turnId: turn.turn_id,
        };
        planMessagesByCallId.set(plan_call_id, planMessage);
        mostRecentPlanCallId.current = plan_call_id;
        output.push(planMessage);
        break;
      }

      case 'plan_decision': {
        const callId =
          event.payload.plan_call_id ?? mostRecentPlanCallId.current;
        const target = callId ? planMessagesByCallId.get(callId) : undefined;
        if (target) {
          target.decision = event.payload.approved ? 'approved' : 'rejected';
        }
        break;
      }

      case 'tool_call': {
        const { tool_call_id, tool_name, args } = event.payload;
        // A repeated tool_call_id (confirmed live: LangGraph replays a node
        // before interrupt() on resume, so record_tool_call can fire twice
        // for one real call — same root cause as the plan_request duplicate
        // handled below) is one logical call, not two. Update the existing
        // record in place rather than pushing a second output entry.
        const existingToolMessage = toolMessagesByCallId.get(tool_call_id);
        if (existingToolMessage) {
          existingToolMessage.toolName = tool_name;
          existingToolMessage.toolArgs = args ?? {};
          break;
        }
        const toolMessage: ToolMessage = {
          sender: 'tool',
          toolCallId: tool_call_id,
          toolName: tool_name,
          toolArgs: args ?? {},
          // No live value applies to restored, concluded data; overwritten
          // by a tool_result below when one exists. A call with no result
          // at all stays 'error' (R3/edge case — never a stale 'running').
          toolStatus: 'error',
          turnId: turn.turn_id,
        };
        toolMessagesByCallId.set(tool_call_id, toolMessage);
        output.push(toolMessage);
        break;
      }

      case 'tool_approval_request': {
        const target = toolMessagesByCallId.get(event.payload.tool_call_id);
        if (target) {
          target.wasGated = true;
        }
        mostRecentGatedToolCallId.current = event.payload.tool_call_id;
        break;
      }

      case 'tool_approval_decision': {
        const callId =
          event.payload.tool_call_id ?? mostRecentGatedToolCallId.current;
        const target = callId ? toolMessagesByCallId.get(callId) : undefined;
        if (target) {
          target.wasGated = true;
          target.gateDecision =
            event.payload.action === 'reject' ? 'rejected' : 'approved';
        }
        break;
      }

      case 'tool_result': {
        const callId = event.payload.tool_call_id;
        const target = callId ? toolMessagesByCallId.get(callId) : undefined;
        if (target) {
          target.toolStatus = event.payload.status;
          // Spec 007: an output that was stored separately arrives as a
          // reference with no text.
          const outputRef = toToolOutputRef(event.payload.reference);
          if (outputRef) {
            target.toolOutputRef = outputRef;
          }
          if (event.payload.result !== undefined) {
            target.toolResult =
              typeof event.payload.result === 'string'
                ? event.payload.result
                : JSON.stringify(event.payload.result);
          }
          // T022 (spec 006 convergence): surface the backend's own measured
          // duration when it reported one — never invented when absent
          // (FR-010).
          if (event.payload.duration_ms !== undefined) {
            target.reportedDurationMs = event.payload.duration_ms;
          }
        }
        break;
      }

      case 'execution_receipt': {
        const { plan_call_id, terminal_status, receipt } = event.payload;
        // One receipt per execution (the server dedupes on plan_call_id);
        // a repeat is the same logical receipt, not a second card.
        if (
          output.some(
            (m) =>
              m.sender === 'execution_receipt' && m.planCallId === plan_call_id,
          )
        ) {
          break;
        }
        output.push({
          sender: 'execution_receipt',
          planCallId: plan_call_id,
          receipt,
          terminalStatus: terminal_status ?? null,
          turnId: turn.turn_id,
        });
        break;
      }

      default:
        break;
    }
  }

  return output;
};

/**
 * Splits the flat plain-message list into legacy "turns": one human
 * message plus everything that followed it, before the next human message
 * — the same one-request-cycle unit a `BackendTurn` covers. Anything
 * before the first human message, if present, forms its own leading
 * bucket rather than being dropped.
 */
const splitIntoLegacyTurns = (
  plainMessages: PlainHistoryMessage[],
): PlainHistoryMessage[][] => {
  const legacyTurns: PlainHistoryMessage[][] = [];
  for (const msg of plainMessages) {
    if (msg.role === 'human' || legacyTurns.length === 0) {
      legacyTurns.push([msg]);
    } else {
      legacyTurns[legacyTurns.length - 1].push(msg);
    }
  }
  return legacyTurns;
};

/**
 * Fallback correlation (used only when `mergeHistoryMessages` can't match
 * by content — see there): the last `sortedBackendTurns.length` legacy
 * turns are assumed to line up 1:1, in order, with the backend's turns.
 * Kept as a defensive fallback, not the primary mechanism, per T021 (spec
 * 006 convergence) — this is the heuristic that motivated adding
 * `user_message` to the backend record in the first place.
 */
const mergeByLegacyPosition = (
  legacyTurns: PlainHistoryMessage[][],
  sortedBackendTurns: BackendTurn[],
  toPlainChatMessage: (msg: PlainHistoryMessage) => ChatMessage,
): ChatMessage[] => {
  const coveredCount = Math.min(sortedBackendTurns.length, legacyTurns.length);
  const uncoveredLegacyTurns = legacyTurns.slice(
    0,
    legacyTurns.length - coveredCount,
  );
  const coveredLegacyTurns = legacyTurns.slice(
    legacyTurns.length - coveredCount,
  );
  // The leading backend turns beyond what legacyTurns can pair with —
  // empty whenever legacyTurns.length >= sortedBackendTurns.length, the
  // mirror image of uncoveredLegacyTurns above (only one of the two is
  // ever non-empty, since coveredCount saturates to whichever side is
  // shorter). Previously silently dropped entirely — not even folded into
  // uncoveredLegacyTurns, since that bucket is built from legacyTurns, not
  // from excess backendTurns (found in review of #989: real data loss —
  // e.g. 1 legacy turn + 3 backend turns kept only the last one).
  const excessBackendTurns = sortedBackendTurns.slice(
    0,
    sortedBackendTurns.length - coveredCount,
  );
  const coveredBackendTurns = sortedBackendTurns.slice(
    sortedBackendTurns.length - coveredCount,
  );

  const output: ChatMessage[] = [];

  // No synthesized user line for these — there is no legacy turn to pair
  // one with (that's exactly why they're excess), and unlike the
  // content-match branch's "no match found" case, position-fallback has
  // already given up on correlating by content at all, so inventing one
  // here would assert a precision this path doesn't have. Render what the
  // backend actually recorded, nothing more.
  for (const backendTurn of excessBackendTurns) {
    output.push(...mapBackendTurnToChatMessages(backendTurn));
  }

  for (const legacyTurn of uncoveredLegacyTurns) {
    for (const msg of legacyTurn) {
      output.push(toPlainChatMessage(msg));
    }
  }

  for (let i = 0; i < coveredCount; i += 1) {
    const legacyTurn = coveredLegacyTurns[i];
    const backendTurn = coveredBackendTurns[i];
    const userMessage = legacyTurn.find((msg) => msg.role === 'human');
    if (userMessage) {
      // Stamped with the covered backend turn's id, same as every message
      // mapBackendTurnToChatMessages produces below — this is a covered
      // turn, not an uncovered legacy one, so it belongs to a real turn
      // (found in review of #989: this was the one message in a covered
      // turn left unstamped, dropping it into turnGrouping.ts's `null`/
      // legacy bucket and out of any exported transcript's turn
      // attribution).
      output.push({
        ...toPlainChatMessage(userMessage),
        turnId: backendTurn.turn_id,
      });
    }

    const backendMessages = mapBackendTurnToChatMessages(backendTurn);
    if (backendMessages.length > 0) {
      output.push(...backendMessages);
    } else {
      // Same fallback as mergeHistoryMessages's content-match branch
      // (found in review of #989, follow-up finding 2): turn_recorder is
      // best-effort and non-transactional (FR-019), so a covered turn can
      // have zero recorded events despite a real, already-delivered
      // plain-text AI answer existing for it. Fall back to the legacy
      // turn's own non-human messages rather than silently discarding
      // an answer the user actually received — stamped with turnId too,
      // same as everything else this turn produces.
      for (const msg of legacyTurn) {
        if (msg.role !== 'human') {
          output.push({
            ...toPlainChatMessage(msg),
            turnId: backendTurn.turn_id,
          });
        }
      }
    }
  }

  return output;
};

/**
 * Merges today's plain-text history mapping with the richer, turn-scoped
 * mapping above, per the "merge-by-turn-presence" rule (contracts/
 * history-data-service.md): for any turn present in `backendTurns`, its
 * mapped content REPLACES the plain-text output for that turn — never both
 * (FR-003) — while any leading portion of the conversation with no
 * turn-scoped record at all keeps today's plain-text output unchanged
 * (FR-012, FR-013).
 *
 * Correlation with the plain `{role, message}` list is by **content**
 * (T021, spec 006 convergence): each backend turn is matched, in order, to
 * the first not-yet-claimed legacy turn whose human message equals that
 * backend turn's `user_message` (analyticsai-server PR #409). Matching
 * walks forward so an earlier legacy turn can never be claimed by a later
 * backend turn, and a repeated human message (e.g. the user saying "yes"
 * twice) resolves to its correct, in-order occurrence.
 *
 * Falls back to the older position/count heuristic (`mergeByLegacyPosition`)
 * only when *every* backend turn lacks a `user_message` — an older row
 * from before that field existed, or one whose plain-text save failed
 * independently (the two stores are non-transactional by design, FR-019 on
 * the backend). A turn that does carry a `user_message` still gets
 * content-matched even when a sibling turn in the same conversation lacks
 * one (found in review of #989, follow-up T034): the per-turn "no match
 * found" branch below already renders an unmatched turn from the backend's
 * own data, so a partial absence degrades one turn at a time rather than
 * forcing the whole conversation onto the coarser position heuristic.
 */
export const mergeHistoryMessages = (
  plainMessages: PlainHistoryMessage[],
  backendTurns: BackendTurn[],
): ChatMessage[] => {
  const toPlainChatMessage = (msg: PlainHistoryMessage): ChatMessage => ({
    sender: msg.role === 'human' ? 'user' : 'ai',
    message: msg.message ?? '',
  });

  if (backendTurns.length === 0) {
    // FR-012: no turn-scoped record at all — behave exactly as today.
    return plainMessages.map(toPlainChatMessage);
  }

  const legacyTurns = splitIntoLegacyTurns(plainMessages);
  const sortedBackendTurns = [...backendTurns].sort(
    (a, b) => a.sequence_no - b.sequence_no,
  );

  const canMatchByContent = sortedBackendTurns.some(
    (t) => typeof t.user_message === 'string' && t.user_message.length > 0,
  );

  if (!canMatchByContent) {
    return mergeByLegacyPosition(
      legacyTurns,
      sortedBackendTurns,
      toPlainChatMessage,
    );
  }

  const output: ChatMessage[] = [];
  let searchFrom = 0;

  for (const backendTurn of sortedBackendTurns) {
    let matchIndex = -1;
    for (let i = searchFrom; i < legacyTurns.length; i += 1) {
      const humanMsg = legacyTurns[i].find((m) => m.role === 'human');
      if (humanMsg && humanMsg.message === backendTurn.user_message) {
        matchIndex = i;
        break;
      }
    }

    if (matchIndex !== -1) {
      // Everything before the match, not yet rendered, is uncovered
      // legacy content — render exactly as today.
      for (let i = searchFrom; i < matchIndex; i += 1) {
        for (const msg of legacyTurns[i]) {
          output.push(toPlainChatMessage(msg));
        }
      }
    }
    // No match found (matchIndex === -1): the backend has this turn but
    // the plain-text save for it is missing or diverged. Render it from
    // the backend's own data anyway, rather than dropping it — the
    // backend record is the one we trust (FR-013).

    // Stamped with turn_id, same as every message
    // mapBackendTurnToChatMessages produces below — this synthesized user
    // message replaces the matched legacy turn's own human message (or
    // stands in when there was no match at all), and it belongs to a real
    // backend turn either way (found in review of #989: left unstamped,
    // this fell into turnGrouping.ts's `null`/legacy bucket and dropped
    // out of any exported transcript's turn attribution).
    output.push({
      sender: 'user',
      message: backendTurn.user_message as string,
      turnId: backendTurn.turn_id,
    });

    const backendMessages = mapBackendTurnToChatMessages(backendTurn);
    if (backendMessages.length > 0 || matchIndex === -1) {
      output.push(...backendMessages);
    } else {
      // matchIndex !== -1 (a real legacy turn matched this one by content)
      // but the backend recorded zero events for it — turn_recorder is
      // best-effort and non-transactional (FR-019), so a turn row +
      // user_message can persist while every event write fails
      // independently. That is a partial-write failure, not "this turn
      // genuinely produced nothing" (the latter is what an empty
      // mapBackendTurnToChatMessages output correctly represents
      // elsewhere). The plain-text store's own AI-side content for this
      // exact turn is real and was already delivered to the user;
      // discarding it here would make an answer the user actually
      // received silently vanish on reopen (found in review of #989,
      // follow-up finding 2). Fall back to the legacy turn's own
      // non-human messages instead of inventing nothing — stamped with
      // turnId too, same as everything else this turn produces, so it
      // groups correctly rather than landing in turnGrouping.ts's
      // `null`/legacy bucket.
      for (const msg of legacyTurns[matchIndex]) {
        if (msg.role !== 'human') {
          output.push({
            ...toPlainChatMessage(msg),
            turnId: backendTurn.turn_id,
          });
        }
      }
    }

    if (matchIndex !== -1) {
      searchFrom = matchIndex + 1;
    }
  }

  // Anything left after the last matched legacy turn is an uncovered
  // trailing portion — render exactly as today.
  for (let i = searchFrom; i < legacyTurns.length; i += 1) {
    for (const msg of legacyTurns[i]) {
      output.push(toPlainChatMessage(msg));
    }
  }

  return output;
};

interface AIModuleProps {
  isVisible: boolean;
  onClose: () => void;
  loopId?: string;
  availableLoops?: { id: string; name: string }[];
  initialChatHistories?: { [loopId: string]: ChatMessage[] };
  setChatHistories: React.Dispatch<
    React.SetStateAction<{ [loopId: string]: ChatMessage[] }>
  >;
}

export interface ToolSuggestion {
  name: string;
  description: string;
}

// Type definitions for the /api/tools response
interface ToolResponse {
  name: string;
  description?: string;
}

interface ToolsApiResponse {
  tools: ToolResponse[];
  total: number;
}

// crypto.randomUUID() is only defined in secure contexts (HTTPS or
// localhost) — undefined on a plain-HTTP internal preview/LAN deployment,
// which would throw and, for the module-level call below, break the whole
// module on import. These ids are UI-only correlation ids (session id,
// plan-card call id) — never sent as security tokens.
const generateUuid = (): string => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  // Fallback using crypto.getRandomValues (still cryptographically secure)
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const array = new Uint8Array(1);
      crypto.getRandomValues(array);
      const r = array[0] % 16;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
  // Last resort fallback (should never happen in modern browsers)
  // Only used if both crypto.randomUUID and crypto.getRandomValues are unavailable
  // These are UI-only correlation IDs, not security tokens
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0; // NOSONAR - Last resort fallback only
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Module-level session ID — survives component remounts.
// Only changes on: page refresh (module reload), or explicit New Chat / Clear.
let _chatSessionId: string = generateUuid();

// Module-level cache of the in-flight/completed tracing-status fetch.
// AIModule is mounted once PER LOOP CARD on the Loops page (LoopHome.tsx
// renders one per loop, hidden ones just get className="hidden"), so without
// this a teamspace with N loops would fire N identical
// GET /api/config/tracing-status calls on page load. Sharing one promise
// across all instances collapses that to a single request.
let _tracingStatusPromise: Promise<boolean> | null = null;

const AIModule: React.FC<AIModuleProps> = ({
  isVisible,
  onClose,
  loopId,
  availableLoops = [],
  initialChatHistories = {},
  setChatHistories,
}) => {
  const { t } = useTranslation();
  const { services } = useConfig();
  const { darkMode } = useTheme();
  const announcementOffset = useAnnouncementOffset();
  const [selectedLoopId, setSelectedLoopId] = useState<string>('');
  const [selectedLoopName, setSelectedLoopName] = useState<string | null>(null);
  const [inputMessage, setInputMessage] = useState<string>('');
  const [isListening, setIsListening] = useState<boolean>(false);
  const [interimTranscript, setInterimTranscript] = useState<string>('');
  const [noLoopWarning, setNoLoopWarning] = useState<string>('');
  const [selectedPrompt, setSelectedPrompt] = useState(-1);
  const [localChatHistories, setLocalChatHistories] =
    useState(initialChatHistories);
  const [isAIBuffering, setIsAIBuffering] = useState<boolean>(false);
  const [showClearCommand, setShowClearCommand] = useState<boolean>(false);
  // Single source of truth lives in transcriptExport so the download sanitizer
  // and the live chat stay in sync (no duplicated literal).
  const LOG_PREFIX = TRANSCRIPT_LOG_PREFIX;

  // Tool suggestion states
  const [showToolSuggestions, setShowToolSuggestions] =
    useState<boolean>(false);

  const [toolSuggestions, setToolSuggestions] = useState<ToolSuggestion[]>([]);
  // Full unfiltered list returned by the last API call — never overwritten by client-side filtering.
  const [allToolSuggestions, setAllToolSuggestions] = useState<
    ToolSuggestion[]
  >([]);
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [isLoadingTools, setIsLoadingTools] = useState<boolean>(false);
  const [toolInputIndex, setToolInputIndex] = useState<number>(-1);
  const [activeTab, setActiveTab] = useState<
    'chats' | 'tasks' | 'history' | 'mcp'
  >('chats');
  // When a task with starters is selected, show starter buttons in chat input
  const [selectedTaskStarters, setSelectedTaskStarters] = useState<null | {
    taskId: string;
    taskName: string;
    conversation_starters: { label: string; prompt: string }[];
  }>(null);

  // Ensure the module always opens on the Chat tab and resets loop selection
  // when opened from the navbar (no loopId prop). This lets the user pick a
  // different loop each time they click the Loop Genie button.
  useEffect(() => {
    if (isVisible) {
      setActiveTab('chats');
      if (!loopId) {
        setSelectedLoopId('');
        setSelectedLoopName(null);
      }
    }
  }, [isVisible]);

  // Add state to store search context per loop with proper typing
  const [searchContexts, setSearchContexts] = useState<{
    [loopId: string]: SearchContext;
  }>({});

  // Socket.IO state for real-time streaming
  const [isSocketIOConnected, setIsSocketIOConnected] =
    useState<boolean>(false);
  const [streamingMessage, setStreamingMessage] = useState<string>('');

  // REMOVED: const currentChatSessionId was stale within the same render cycle.
  // All call-sites now read _chatSessionId directly so mid-render mutations are visible.

  // Purely a re-render trigger for the thread-id badge below — _chatSessionId
  // itself is never copied into state (see comment above; that caused the
  // original staleness bug). The counter's value is never read, only bumped.
  const [, bumpSessionIdVersion] = useState(0);

  const { user, isAuthenticated } = useUserAuth();
  const { teamspaceId, teamspaceName, tenantId } = useTeamspaceContext();
  // Same source the profile menu and integrations page use for tenant-service calls.
  const tenantBaseUrl = services?.tenant?.url ?? '';
  const isGeneratingRef = useRef(false);
  const processingLoopIdRef = useRef<string | null>(null);
  // Execution events a screen reader has already been told about. Kept here, not in
  // ChatThread: ChatThread is only mounted while the Chats tab is shown, so state
  // inside it is reset every time the user leaves and returns to the tab (review of
  // #1054).
  const [announcedExecutionKeys, setAnnouncedExecutionKeys] = useState<
    string[]
  >([]);
  const handleExecutionAnnounced = useCallback((key: string) => {
    setAnnouncedExecutionKeys((prev) =>
      prev.includes(key) ? prev : [...prev, key],
    );
  }, []);
  const selectedLoopIdRef = useRef(selectedLoopId);
  const prevBufferingRef = useRef(isAIBuffering);
  const localChatHistoriesRef = useRef(localChatHistories);
  // Identifies the current turn (one user request + everything the agent
  // does in response). Minted fresh each time handleSendMessage runs and
  // carried onto every message the turn produces via stampTurn() below —
  // never derived from array position, since isTransientMessage trimming,
  // the plan-expiry rewrite, and streaming-buffer replacement all splice
  // this list mid-turn.
  const currentTurnIdRef = useRef<string | null>(null);

  // Stamps a freshly-created message with the current turn id. Messages
  // without a stamp (anything created before this feature, or restored from
  // history) group into one legacy turn instead of being dropped or guessed
  // at (FR-002).
  const stampTurn = <T extends ChatMessage>(entry: T): T =>
    currentTurnIdRef.current
      ? { ...entry, turnId: currentTurnIdRef.current }
      : entry;
  const stopRequestedRef = useRef<boolean>(false);
  const lastTaskIdSentRef = useRef<string | null>(null);
  const lastTaskIconRef = useRef<string | null>(null);
  const lastTaskNameRef = useRef<string | null>(null);
  const [activeTaskInfo, setActiveTaskInfo] = useState<{
    name: string;
    icon?: TaskIcon;
  } | null>(null);
  // Keyed by tool_call_id — the model can dispatch several mutating calls in
  // parallel, each pausing as its own tool_approval_request interrupt, so
  // more than one can be pending at once (see markHitlRowSettled/
  // settleAllPendingHitl below).
  const [hitlApprovals, setHitlApprovals] = useState<
    Record<string, ToolApprovalRequest>
  >({});
  // Keeps hitlApprovals readable inside socket-event closures without stale
  // captures, mirroring planApprovalRef below — needed to know which inline
  // tool-call rows to settle when a decision is made or the turn is
  // abandoned.
  const hitlApprovalsRef = useRef<Record<string, ToolApprovalRequest>>({});
  // A short-lived snapshot of each request removed from hitlApprovals when
  // its decision is sent, keyed by tool_call_id. Normally moot — the row
  // settles for real once a tool_call_result/chat_complete arrives — but if
  // the backend rejects the send outright (turn_in_flight: another decision
  // from this same batch is still being processed), there is otherwise no
  // way to put that row back to a decidable state, and it would spin
  // forever. Entries are deleted once consumed by that revert, or wiped
  // wholesale on settleAllPendingHitl.
  const pendingHitlSendCacheRef = useRef<Record<string, ToolApprovalRequest>>(
    {},
  );
  // The plan decision most recently sent, kept until the server takes it: a
  // refused approval (approval_refused) puts the card back from this. Only one
  // plan awaits an answer at a time. `cardId` is the UI's own id for the
  // history entry (not the server's plan_call_id).
  const pendingPlanSendRef = useRef<{
    request: PlanApprovalRequest;
    cardId: string | null;
  } | null>(null);
  // True from the moment a HITL decision is sent until the backend either
  // pauses again (a fresh tool_approval_request — safe to decide the next
  // row) or the turn ends (chat_complete/error). A single "approve" can now
  // silently resolve many still-pending rows in one background cascade (a
  // grant covering the rest of a parallel batch), which can take tens of
  // seconds against a slow real backend — disable the remaining rows for
  // that window so a second click can't race the backend's one-turn-per-
  // conversation guard and get rejected as "already in progress".
  const [isHitlTurnInFlight, setIsHitlTurnInFlight] = useState(false);
  const [planApproval, setPlanApproval] = useState<PlanApprovalRequest | null>(
    null,
  );
  // Ref keeps planApproval readable inside socket-event closures without stale
  // captures. Updated synchronously in every set-site so back-to-back events
  // see the current value even before the next React commit.
  const planApprovalRef = useRef<PlanApprovalRequest | null>(null);
  // Identifies which persisted 'plan_approval' history entry the pending
  // decision belongs to, so handlePlanApprove/Reject/RequestChanges can
  // update that exact entry (not just clear the live planApproval state).
  const pendingPlanCallIdRef = useRef<string | null>(null);
  const [iterationWarning, setIterationWarning] =
    useState<IterationWarningEvent | null>(null);
  const [isStopping, setIsStopping] = useState(false);
  const [activeClarification, setActiveClarification] =
    useState<ClarificationRequest | null>(null);
  const [contextWindow, setContextWindow] = useState<{
    used: number;
    max: number;
    pct: number;
    model: string;
    estimated?: boolean;
  } | null>(null);

  const userRef = useRef(user);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const recognitionRef = useRef<any>(null);
  const interimTranscriptRef = useRef<string>('');

  useEffect(() => {
    userRef.current = user;
    isAuthenticatedRef.current = isAuthenticated;
  }, [user, isAuthenticated]);

  // Keep planApprovalRef in sync via useEffect as a fallback; the primary
  // sync is done synchronously at every call-site to avoid commit-lag issues.
  useEffect(() => {
    planApprovalRef.current = planApproval;
  }, [planApproval]);
  useEffect(() => {
    hitlApprovalsRef.current = hitlApprovals;
  }, [hitlApprovals]);

  const aiService = useMemo(
    () =>
      new AiDataService(
        () => userRef.current,
        () => isAuthenticatedRef.current,
      ),
    [],
  );

  // Whether LangSmith tracing is configured on the backend (all 4
  // LANGSMITH_* env vars set) — gates the thread-id badge in ChatHeader.
  // Fails closed (stays false) on any error; cosmetic capability check only.
  const [tracingEnabled, setTracingEnabled] = useState<boolean>(false);

  useEffect(() => {
    if (!isVisible) return;
    let cancelled = false;
    _tracingStatusPromise ??= Promise.resolve(
      aiService.getTracingStatus(),
    ).catch(() => false);
    _tracingStatusPromise.then((enabled: boolean) => {
      if (!cancelled) setTracingEnabled(enabled);
    });
    return () => {
      cancelled = true;
    };
  }, [aiService, isVisible]);

  // Whether the user still has to say which integration AI features should use.
  // Only true when the choice is genuinely ambiguous: several reachable paths and no
  // valid saved selection. A user with one path — which is every user who existed
  // before multi-group membership — is never prompted.
  const [needsIntegrationChoice, setNeedsIntegrationChoice] = useState(false);
  // Set once the preferences lookup succeeds and finds nothing usable: no group, or only
  // disabled integrations (a disabled path is listed but grants nothing).
  const [integrationIssue, setIntegrationIssue] =
    useState<IntegrationAccessIssue | null>(null);

  // Stable identities. The modal's fetch effect keys on these props, so inline arrows
  // would give it a new `load` on every AIModule render — re-reading preferences, and
  // re-POSTing the default in its single-option branch, for as long as it stayed open.
  const getIntegrationUserContext = useCallback(
    () => ({ access_token: userRef.current?.access_token }),
    [],
  );
  const closeIntegrationChoice = useCallback(
    () => setNeedsIntegrationChoice(false),
    [],
  );

  // Re-reads the user's integration access. Returns a canceller for the caller to run on
  // cleanup, so a slow response cannot overwrite state after the panel has moved on.
  const refreshIntegrationAccess = useCallback(() => {
    if (!isVisible || !teamspaceId || !tenantBaseUrl) return () => {};
    let cancelled = false;
    IntegrationPreferencesService.getInstance(
      tenantBaseUrl,
      getIntegrationUserContext,
    )
      .getPreferences(teamspaceId)
      .then((prefs) => {
        if (cancelled) return;
        setNeedsIntegrationChoice(prefs.requiresSelection);
        setIntegrationIssue(integrationAccessIssue(prefs.options ?? []));
      })
      // Fail closed: a preferences lookup that errors must not block the chat. The
      // backend still resolves a deterministic fallback path on its own, and a failure
      // says nothing about group membership, so no notice either.
      .catch(() => {
        if (cancelled) return;
        setNeedsIntegrationChoice(false);
        setIntegrationIssue(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isVisible, teamspaceId, tenantBaseUrl, getIntegrationUserContext]);

  useEffect(() => refreshIntegrationAccess(), [refreshIntegrationAccess]);

  // The default can be changed from the Member Access page while this panel stays open;
  // without this the gate would keep showing the state it read when the panel opened.
  useEffect(() => {
    const onChanged = () => {
      refreshIntegrationAccess();
    };
    window.addEventListener(DEFAULT_INTEGRATION_CHANGED_EVENT, onChanged);
    return () =>
      window.removeEventListener(DEFAULT_INTEGRATION_CHANGED_EVENT, onChanged);
  }, [refreshIntegrationAccess]);

  const mcpDataService = useMemo(
    () =>
      new MCPDataService(
        () => userRef.current,
        () => isAuthenticatedRef.current,
        services?.analyticsai?.url,
      ),
    [services?.analyticsai?.url],
  );

  // Spec 007: lets a tool row download the full output of its call. A row carries the
  // conversation it belongs to (stamped when the result arrives or the chat is restored);
  // the module-level _chatSessionId is only the fallback for a row without one.
  const toolOutputDownload = useMemo(
    () => ({
      download: async (toolCallId: string, conversationId?: string) => {
        try {
          const blob = await aiService.downloadToolOutput(
            conversationId || _chatSessionId,
            toolCallId,
          );
          saveBlobAsFile(blob, `tool-output-${toolCallId}.txt`);
        } catch (error) {
          const status = (error as { status?: number } | undefined)?.status;
          if (status === 404) {
            throw new Error('This output is no longer available.');
          }
          if (status === 503) {
            throw new Error(
              'The stored output is temporarily unavailable. Try again later.',
            );
          }
          throw new Error('The download failed. Try again.');
        }
      },
    }),
    [aiService],
  );

  // Subscribed on `teamspaceId` alone, NOT gated by `isVisible`: this panel
  // toggles visible/hidden on the same mounted instance (nav icon open/close)
  // rather than unmounting, and useIngestJobsPolling resets its tracked jobs
  // to [] on every dependency change. Gating on isVisible therefore wiped and
  // re-raced the job list on every close/reopen, letting LoopGenie briefly
  // read as "usable" right after reopening even while an MCP server was
  // still indexing (NEXUS00007214).
  const { jobs: ingestJobs } = useIngestJobsPolling(
    mcpDataService,
    teamspaceId,
  );
  const isIngestionActive = ingestJobs.some(
    (job) => job.status === 'queued' || job.status === 'running',
  );
  // Stale-closure guard: a task-starter button persisted into chat history by
  // appendTaskDivider/onTaskSelected freezes whatever `handleTaskSendToChat`
  // instance (and its closed-over `isIngestionActive`) existed at the render
  // when the task was selected. Reading a ref instead — kept current via the
  // effect below — lets that same stale closure still observe ingestion
  // state as of the click, not as of task selection. Mirrors the
  // userRef/isAuthenticatedRef pattern above.
  const isIngestionActiveRef = useRef(isIngestionActive);

  useEffect(() => {
    isIngestionActiveRef.current = isIngestionActive;
  }, [isIngestionActive]);

  // --- Drag-resize state ---
  const INITIAL_WIDTH_PERCENT = 40;
  const MAX_WIDTH_PERCENT = 70;
  const MIN_WIDTH_PERCENT = 40;
  const [panelWidthPercent, setPanelWidthPercent] = useState(
    INITIAL_WIDTH_PERCENT,
  );
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(INITIAL_WIDTH_PERCENT);

  // Reset width every time the panel opens
  useEffect(() => {
    if (isVisible) {
      setPanelWidthPercent(INITIAL_WIDTH_PERCENT);
    }
  }, [isVisible]);

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isResizingRef.current = true;
      startXRef.current = e.clientX;
      startWidthRef.current = panelWidthPercent;
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    },
    [panelWidthPercent],
  );

  useEffect(() => {
    const handleResizeMouseMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return;
      const deltaX = startXRef.current - e.clientX;
      const viewportWidth = window.innerWidth;
      const deltaPercent = (deltaX / viewportWidth) * 100;
      const newWidth = Math.min(
        MAX_WIDTH_PERCENT,
        Math.max(MIN_WIDTH_PERCENT, startWidthRef.current + deltaPercent),
      );
      setPanelWidthPercent(newWidth);
    };

    const handleResizeMouseUp = () => {
      if (isResizingRef.current) {
        isResizingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    document.addEventListener('mousemove', handleResizeMouseMove);
    document.addEventListener('mouseup', handleResizeMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleResizeMouseMove);
      document.removeEventListener('mouseup', handleResizeMouseUp);
      if (isResizingRef.current) {
        isResizingRef.current = false;
      }
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, []);
  // --- End drag-resize ---

  const isTransientMessage = (msg: any) => {
    if (!msg || msg.sender !== 'ai') return false;
    if (msg.isStreaming) return true;
    const text = msg.message;

    if (text.startsWith(LOG_PREFIX)) return false;

    return (
      text === t('ai-buffer-msg') ||
      text === t('searching-buffer-msg') ||
      text.startsWith(t('processing-ai')) ||
      text.startsWith(t('validating')) ||
      text.startsWith(t('using-tool')) ||
      text.startsWith(t('validating')) ||
      text.startsWith(t('plan-complete'))
    );
  };

  const handleClearCommand = async () => {
    if (!selectedLoopId) {
      setNoLoopWarning(t('no-loop-selected'));
      return;
    }

    try {
      lastTaskIdSentRef.current = null;
      lastTaskIconRef.current = null;
      lastTaskNameRef.current = null;
      setActiveTaskInfo(null);
      setContextWindow(null);
      // Clear any pending clarification/approval from the previous session
      setActiveClarification(null);
      setHitlApprovals({});
      hitlApprovalsRef.current = {};
      setPlanApproval(null);
      setIterationWarning(null);
      SocketService.disconnect();
      // Use clearChatOptimistic with regenerateSessionId=true AND deleteFromBackend=true for explicit /clear
      clearChatOptimistic(selectedLoopId, undefined, true, true);
    } catch (error) {
      setNoLoopWarning(t('failed-to-clear-chat'));
    }
  };

  const handleNewChat = () => {
    if (!selectedLoopId) {
      setNoLoopWarning(t('no-loop-selected'));
      return;
    }

    lastTaskIdSentRef.current = null;
    lastTaskIconRef.current = null;
    lastTaskNameRef.current = null;
    setActiveTaskInfo(null);
    setContextWindow(null);
    setSelectedTool(null);
    // Clear any pending clarification/approval from the previous session so the
    // next message is treated as a fresh chat instead of a resume.
    setActiveClarification(null);
    setHitlApprovals({});
    hitlApprovalsRef.current = {};
    setPlanApproval(null);
    setIterationWarning(null);
    SocketService.disconnect();

    // Only clear local UI state and generate a new session ID.
    // Do NOT call the backend clear endpoint — the old session should be
    // preserved in history so the user can return to it later.
    const updatedHistories = {
      ...localChatHistories,
      [selectedLoopId]: [],
    };
    updateChatHistories(updatedHistories);
    setSearchContexts((prev) => ({
      ...prev,
      [selectedLoopId]: {
        results: { hits: [] },
        renderedResults: null,
        searchContext: '',
        hasSearched: false,
      },
    }));
    setShowClearCommand(false);
    setInputMessage('');
    setSelectedPrompt(-1);
    setNoLoopWarning('');
    _chatSessionId = generateUuid();
    bumpSessionIdVersion((v) => v + 1);
    setActiveTab('chats');
  };
  const formatAIResponse = (message: string): string => {
    if (!message) return '';

    const firstOpen = message.indexOf('{');
    const lastClose = message.lastIndexOf('}');

    if (firstOpen !== -1 && lastClose !== -1 && lastClose > firstOpen) {
      const candidateJson = message.slice(firstOpen, lastClose + 1);

      try {
        const parsed = JSON.parse(candidateJson);

        let cleanDataText = null;

        if (parsed.data && typeof parsed.data === 'string') {
          cleanDataText = parsed.data;
        } else if (Array.isArray(parsed.data)) {
          const stepsText = parsed.data
            .map((step: any) => {
              if (typeof step.data === 'string') return step.data;
              if (typeof step.message === 'string') return step.message;
              return null;
            })
            .filter(Boolean);

          if (stepsText.length > 0) {
            cleanDataText = stepsText.join('\n\n');
          }
        } else if (
          parsed.execution_result?.data &&
          typeof parsed.execution_result.data === 'string'
        ) {
          cleanDataText = parsed.execution_result.data;
        }

        if (cleanDataText) {
          let textBefore = message.slice(0, firstOpen).trim();
          textBefore = textBefore.replace(/```(?:json)?$/i, '').trim();

          let textAfter = message.slice(lastClose + 1).trim();
          textAfter = textAfter.replace(/^```/, '').trim();

          let header = textBefore;
          if (!header) {
            header = `**${t('execution-result')}**`;
          } else if (!header.includes('**')) {
            header = `**${header}**`;
          }

          return `${header}\n\n${cleanDataText}\n\n${textAfter}`;
        }
      } catch (e) {
        return message;
      }
    }
    return message;
  };

  // Transform backend [[TASK_ACTIVATED]] control messages into AI dividers so
  // they never appear as raw user bubbles in the chat history.
  const normalizeActivationMessages = (msgs: ChatMessage[]): ChatMessage[] =>
    msgs.map((msg) => {
      if (
        msg.sender !== 'tool' &&
        msg.sender === 'user' &&
        isActivationContent(msg.message)
      ) {
        return {
          sender: 'ai' as const,
          message: stripActivationMarker(msg.message),
        };
      }
      return msg;
    });

  useEffect(() => {
    if (isAIBuffering) return;
    setLocalChatHistories((prev) => {
      const loopKey = selectedLoopIdRef.current || selectedLoopId || '';
      // Normalize activation markers in the incoming histories
      const normalizedIncoming: { [loopId: string]: ChatMessage[] } = {};
      for (const key of Object.keys(initialChatHistories)) {
        normalizedIncoming[key] = normalizeActivationMessages(
          initialChatHistories[key] || [],
        );
      }
      if (loopKey) {
        const prevLen = (prev[loopKey] || []).length;
        const incomingLen = (normalizedIncoming[loopKey] || []).length;
        if (prevLen > incomingLen) {
          return { ...normalizedIncoming, [loopKey]: prev[loopKey] } as any;
        }
      }
      return normalizedIncoming as any;
    });
  }, [initialChatHistories]);

  // Keep the ref in sync with the latest localChatHistories
  useEffect(() => {
    localChatHistoriesRef.current = localChatHistories;
  }, [localChatHistories]);

  useEffect(() => {
    if (prevBufferingRef.current && !isAIBuffering && selectedLoopId) {
      setChatHistories(localChatHistoriesRef.current);
    }
    prevBufferingRef.current = isAIBuffering;
  }, [isAIBuffering, setChatHistories, selectedLoopId]);

  // Scroll-to-bottom is handled by MessageScroller's scrollAnchor prop in ChatThread.

  // Store the complete list of available tools
  const [allAvailableTools, setAllAvailableTools] = useState<string[]>([]);

  const fetchToolsFromAPI = async () => {
    if (!teamspaceId) {
      setShowToolSuggestions(false);
      setIsLoadingTools(false);
      return;
    }
    setIsLoadingTools(true);
    try {
      const { tools } = (await aiService.fetchTools(
        teamspaceId,
      )) as ToolsApiResponse;
      if (Array.isArray(tools) && tools.length > 0) {
        const suggestions = tools.map((t) => ({
          name: t.name,
          description: t.description || '',
        }));
        setAllAvailableTools(tools.map((t) => t.name));
        setAllToolSuggestions(suggestions);
        setToolSuggestions(suggestions);
      } else {
        setAllAvailableTools([]);
        setAllToolSuggestions([]);
        setToolSuggestions([]);
      }
    } catch (error) {
      setAllAvailableTools([]);
      setAllToolSuggestions([]);
      setToolSuggestions([]);
    } finally {
      setIsLoadingTools(false);
    }
  };

  // Handle tool selection from suggestions
  const handleToolSelection = (tool: ToolSuggestion) => {
    const textBeforeHash = inputMessage.substring(0, toolInputIndex);
    setInputMessage(`${textBeforeHash}#${tool.name} `);
    setSelectedTool(tool.name);
    setInputMessage('');
    setShowToolSuggestions(false);
  };

  useEffect(() => {
    if (loopId && !selectedLoopId) {
      const loop = availableLoops.find((loop) => loop.id === loopId);
      setSelectedLoopId(loopId);
      setSelectedLoopName(loop ? loop.name : null);
    } else if (!selectedLoopId) {
      setSelectedLoopId('');
      setSelectedLoopName(null);
    }
  }, [loopId, availableLoops]);

  useEffect(() => {
    selectedLoopIdRef.current = selectedLoopId;
  }, [selectedLoopId]);

  // Socket.IO connection and event listeners
  useEffect(() => {
    const handleStatusChange = ({
      isConnected,
      reason,
    }: {
      isConnected: boolean;
      reason?: string;
    }) => {
      setIsSocketIOConnected(isConnected);

      if (!isConnected && isGeneratingRef.current) {
        const targetLoopId = processingLoopIdRef.current || selectedLoopId;

        if (!targetLoopId) return;

        isGeneratingRef.current = false;
        setIsAIBuffering(false);
        setStreamingMessage('');
        processingLoopIdRef.current = null;

        // A disconnect mid-turn otherwise leaves any tool call already past
        // its approval (toolStatus 'running', or still 'pending_approval' if
        // the disconnect raced the decision) stuck spinning forever — no
        // tool_call_result can ever arrive over a dead socket, and nothing
        // else revisits these rows once they're out of hitlApprovals. Settle
        // them here, at the moment connectivity is actually lost, rather
        // than leaving stale 'running' state for a later history-reload's
        // merge (AIModule.tsx's initialChatHistories-effect) to potentially
        // preserve as-is.
        //
        // The history update below runs *before* settleAllPendingHitl, not
        // after: both queue functional updates to the same state in this one
        // synchronous handler, so whichever runs first is the one whose
        // toolResult text actually lands (found in review of #989) —
        // markHitlRowSettled's own terminal-state guard (see there) means it
        // no-ops on a row this update already moved to 'error', so ordering
        // it first is what lets a gated call keep this handler's specific
        // "connection-disconnected" message instead of settleAllPendingHitl's
        // generic "No longer pending." landing on top of it. settleAllPendingHitl
        // is also passed targetLoopId explicitly — its default
        // (selectedLoopIdRef.current) is the currently-*viewed* loop, which is
        // wrong here if the user has since navigated away from the loop that
        // actually disconnected (found in review of #989).
        const hadActivePlan = planApprovalRef.current !== null;

        setLocalChatHistories((prev) => {
          const currentHistory = prev[targetLoopId] || [];
          const lastMsg = currentHistory[currentHistory.length - 1];

          const isBufferMessage =
            lastMsg &&
            lastMsg.sender === 'ai' &&
            (lastMsg.message === t('ai-buffer-msg') ||
              lastMsg.message.startsWith(t('processing-ai')) ||
              lastMsg.message.startsWith(t('using-tool')) ||
              lastMsg.message.startsWith(t('validating')));

          const historyWithoutBuffer = isBufferMessage
            ? currentHistory.slice(0, -1)
            : currentHistory;

          // Any tool call still 'running' (approved, no result yet) or
          // 'pending_approval' (the disconnect raced markHitlRowSettled)
          // never gets a result now — settle it rather than leave it
          // spinning with no path back to a terminal state.
          const historyWithSettledTools = historyWithoutBuffer.map((msg) =>
            msg.sender === 'tool' &&
            (msg.toolStatus === 'running' ||
              msg.toolStatus === 'pending_approval')
              ? {
                  ...msg,
                  toolStatus: 'error' as const,
                  toolResult: t('connection-disconnected'),
                }
              : msg,
          );

          return {
            ...prev,
            [targetLoopId]: [
              ...historyWithSettledTools,
              stampTurn({
                sender: 'ai' as const,
                message: `${t('connection-disconnected')} (${reason || t('network-error')}). ${t('please-try-again')}`,
              }),
            ],
          };
        });

        settleAllPendingHitl(targetLoopId);
        setPlanApproval(null);
        planApprovalRef.current = null;
        if (hadActivePlan) markPlanDecided('expired');
      }
    };

    SocketService.onConnectionChange(handleStatusChange);

    // Register event listeners for streaming updates

    // Cleanup on unmount
    return () => {
      SocketService.removeConnectionListener(handleStatusChange);
      setIsSocketIOConnected(false);
    };
  }, [settings.useSocketIO]);

  const SOCKET_RETRY_DELAY_MS = 100;
  const initializeSocketListeners = () => {
    if (!SocketService.isConnected()) {
      setTimeout(() => {
        initializeSocketListeners();
      }, SOCKET_RETRY_DELAY_MS);
      return;
    }
    // This is called on every message send, not just on first connect, and
    // connect() reuses the existing socket whenever one is already
    // connected. Without this guard, every send bound a full extra set of
    // listeners onto the same long-lived socket, so a single real backend
    // event (e.g. plan_approval_request) fired once per accumulated
    // listener -- surfacing as duplicate/phantom tool and plan-approval
    // cards that kept appearing with no further user action.
    if (SocketService.listenersAlreadyBound()) {
      return;
    }
    const getCurrentUsername = () => {
      return userRef.current?.profile?.preferred_username;
    };
    aiService.onWorkflowEvent((event: any) => {
      if (stopRequestedRef.current) {
        return;
      }
      let displayMessage = '';
      if (event.event === 'node_started') {
        const message = event.data?.message;
        const node = event.data?.node;
        displayMessage = message || t('ai-working-on-node', { node });
      } else if (event.event === 'node_completed') {
        const message = event.data?.message;
        displayMessage = `${message || ''}`;
      } else if (event.event === 'node_updated') {
        const message = event.data?.message;
        displayMessage = `${message || ''}`;
      } else if (event.event === 'plan_created') {
        // The plan data is nested inside event.data.plan
        const planData = event.data?.plan || {};
        const currentLoopId = selectedLoopIdRef.current;
        const username = getCurrentUsername();
        const expectedCompositeId = `${_chatSessionId}:${username}`;
        if (event.data.room_id !== expectedCompositeId) {
          return;
        }
        const targetLoopId = currentLoopId;

        setStreamingMessage('');
        const planComponent = (
          <ExecutionPlan
            agent_description={planData.agent_description}
            plan_summary={planData.plan_summary}
            total_steps={planData.total_steps}
            estimated_duration={planData.estimated_duration}
            complexity={planData.complexity}
            risk_level={planData.risk_level}
            steps={planData.execution_plan}
            requires_approval={planData.requires_approval}
          />
        );

        // Add the plan to chat history WITHOUT removing it later
        setLocalChatHistories((prev) => {
          const currentHistory = prev[targetLoopId] || [];
          const lastMsg = currentHistory[currentHistory.length - 1];

          // Remove the last AI buffer message if present
          const historyWithoutBuffer = isTransientMessage(lastMsg)
            ? currentHistory.slice(0, -1)
            : currentHistory;

          return {
            ...prev,
            [targetLoopId]: [
              ...historyWithoutBuffer,
              stampTurn({
                sender: 'ai' as const,
                message: t('execution-plan-heading'),
                subMessage: planComponent,
              }),
              stampTurn({
                sender: 'ai' as const,
                message: t('processing-execute-plan'),
              }),
            ],
          };
        });
        setIsAIBuffering(true);
        return;
      } else if (event.event === 'THINKING_CHUNK') {
        const chunkText: string = event.data?.text || '';
        if (!chunkText) return;
        setLocalChatHistories((prev) => {
          const targetLoopId = selectedLoopIdRef.current || '';
          if (!targetLoopId) return prev;
          const currentHistory = prev[targetLoopId] || [];
          const lastMsg = currentHistory[currentHistory.length - 1];
          if (
            lastMsg?.sender === 'ai' &&
            lastMsg.message.startsWith(LOG_PREFIX)
          ) {
            const newHistory = [...currentHistory];
            newHistory[newHistory.length - 1] = {
              ...lastMsg,
              message: lastMsg.message + chunkText,
            };
            return { ...prev, [targetLoopId]: newHistory };
          }
          const historyWithoutBuffer = isTransientMessage(lastMsg)
            ? currentHistory.slice(0, -1)
            : currentHistory;
          return {
            ...prev,
            [targetLoopId]: [
              ...historyWithoutBuffer,
              stampTurn({
                sender: 'ai' as const,
                message: LOG_PREFIX + chunkText,
              }),
            ],
          };
        });
        return;
      } else if (event.event === 'SUMMARIZE_START') {
        return;
      } else if (event.event === 'SUMMARIZE_CHUNK') {
        const chunkText: string = event.data?.text || '';
        if (!chunkText) return;
        setLocalChatHistories((prev) => {
          const targetLoopId = selectedLoopIdRef.current || '';
          if (!targetLoopId) return prev;
          const currentHistory = prev[targetLoopId] || [];
          const lastMsg = currentHistory[currentHistory.length - 1];
          if (
            (lastMsg?.sender === 'user' || lastMsg?.sender === 'ai') &&
            lastMsg.isStreaming
          ) {
            const newHistory = [...currentHistory];
            newHistory[newHistory.length - 1] = {
              ...lastMsg,
              message: lastMsg.message + chunkText,
            };
            return { ...prev, [targetLoopId]: newHistory };
          }
          const historyWithoutBuffer = isTransientMessage(lastMsg)
            ? currentHistory.slice(0, -1)
            : currentHistory;
          return {
            ...prev,
            [targetLoopId]: [
              ...historyWithoutBuffer,
              stampTurn({
                sender: 'ai' as const,
                message: chunkText,
                isStreaming: true,
              }),
            ],
          };
        });
        return;
      } else if (event.event === 'SUMMARIZE_COMPLETE') {
        return;
      } else if (event.event === 'tool_call_start') {
        const { tool_call_id, tool, args } = event.data || {};
        if (!tool_call_id) return;
        // propose_plan is a HITL mechanism — the plan approval card surfaces it;
        // don't also show it as a tool call chip. A narrower check than
        // turnGrouping.ts's own INTERNAL_TOOL_NAMES/isInternalToolName on
        // purpose: search_tools IS internal (hidden from the visible trace
        // by that filter) but still needs a message to exist so
        // transcriptExport.ts can keep it (AC10) — gating on the full
        // internal-name set here suppressed search_tools before any message
        // was ever created, silently dropping it from the transcript too
        // (found in review of #988, fifth round). Still one shared list,
        // not a second independently-maintained copy — just the narrower
        // one from turnGrouping.ts, not the display filter's.
        if (isSuppressedBeforeMessage(tool)) return;
        flushSync(() => {
          setLocalChatHistories((prev) => {
            const loopId = selectedLoopIdRef.current || '';
            if (!loopId) return prev;
            const history = prev[loopId] || [];
            // Upsert by id: an interrupted call (paused for approval)
            // replays its tool_call_start on resume — same id, same row,
            // reset to running — rather than appending a visual duplicate.
            const existingIndex = history.findIndex(
              (msg) => msg.sender === 'tool' && msg.toolCallId === tool_call_id,
            );
            // wasGated and gateDecision carry over across a replay
            // (interrupted for approval, then approved, then the backend
            // resumes and re-dispatches the same call) — a new
            // tool_call_start for the same id must not forget the call was
            // gated OR forget the decision that was already made. Missing
            // gateDecision here left an approved call looking permanently
            // undecided (S3b hides on wasGated-without-gateDecision), so
            // every approved call stayed pinned in the column with no
            // outcome marker and no summary-line credit — found live, after
            // deploying S3b (see research.md R4 addendum). startedAt does
            // NOT carry over: a replay means the tool is only now starting
            // to execute, and the approval wait must not be counted as tool
            // duration (FR-012).
            const existingMsg =
              existingIndex !== -1 ? history[existingIndex] : undefined;
            const priorWasGated =
              existingMsg?.sender === 'tool' ? existingMsg.wasGated : undefined;
            const priorGateDecision =
              existingMsg?.sender === 'tool'
                ? existingMsg.gateDecision
                : undefined;
            const freshEntry = stampTurn({
              sender: 'tool' as const,
              toolCallId: tool_call_id,
              toolName: tool || '',
              toolArgs: args || {},
              toolStatus: 'running' as const,
              startedAt: Date.now(),
              ...(priorWasGated ? { wasGated: true as const } : {}),
              ...(priorGateDecision ? { gateDecision: priorGateDecision } : {}),
            });
            if (existingIndex === -1) {
              return { ...prev, [loopId]: [...history, freshEntry] };
            }
            const next = history.slice();
            next[existingIndex] = freshEntry;
            return { ...prev, [loopId]: next };
          });
        });
        return;
      } else if (event.event === 'tool_call_result') {
        const { tool_call_id, result, status, blocks, reference } =
          event.data || {};
        if (!tool_call_id) return;
        // A call that resolves on its own — approved by something other
        // than a click on its own card (a wider grant scope, or the plan
        // approval covering an already-gated mutation) — never goes through
        // onHitlDecide, so nothing has removed it from hitlApprovals yet.
        // Left in place, settleAllPendingHitl (run at turn end and on
        // several abandonment paths) would still find it "pending" and
        // stamp it back to toolStatus: 'error' even though it just
        // succeeded — found live: an approved-by-plan mutation reported a
        // false failure the moment the turn's final answer arrived. A
        // resolved call is never pending again, so drop it here too, not
        // only on a user decision.
        if (tool_call_id in hitlApprovalsRef.current) {
          const next = { ...hitlApprovalsRef.current };
          delete next[tool_call_id];
          hitlApprovalsRef.current = next;
          setHitlApprovals((prev) => {
            if (!(tool_call_id in prev)) return prev;
            const nextState = { ...prev };
            delete nextState[tool_call_id];
            return nextState;
          });
        }
        setLocalChatHistories((prev) => {
          const loopId = selectedLoopIdRef.current || '';
          if (!loopId) return prev;
          return {
            ...prev,
            [loopId]: (prev[loopId] || []).map((msg) =>
              msg.sender === 'tool' && msg.toolCallId === tool_call_id
                ? {
                    ...msg,
                    toolResult: result || '',
                    toolResultBlocks: blocks || [],
                    toolOutputRef: toToolOutputRef(reference, _chatSessionId),
                    toolStatus: (status === 'error' ? 'error' : 'success') as
                      | 'error'
                      | 'success',
                    endedAt: Date.now(),
                  }
                : msg,
            ),
          };
        });
        return;
      } else if (event.event === 'context_window_update') {
        const d = event.data;
        setContextWindow({
          used: d.used,
          max: d.max,
          pct: d.pct,
          model: d.model,
          estimated: d.estimated,
        });
        return;
      }

      if (displayMessage) {
        setLocalChatHistories((prev) => {
          const targetLoopId = selectedLoopIdRef.current || '';
          if (!targetLoopId) return prev;

          const currentHistory = prev[targetLoopId] || [];
          const lastMsg = currentHistory[currentHistory.length - 1];

          if (
            lastMsg &&
            lastMsg.sender === 'ai' &&
            lastMsg.message.startsWith(LOG_PREFIX)
          ) {
            if (lastMsg.message.endsWith(displayMessage)) return prev;

            const newLogContent = lastMsg.message + '\n' + displayMessage;
            const newHistory = [...currentHistory];
            newHistory[newHistory.length - 1] = {
              ...lastMsg,
              message: newLogContent,
            };

            return { ...prev, [targetLoopId]: newHistory };
          } else {
            const historyWithoutBuffer = isTransientMessage(lastMsg)
              ? currentHistory.slice(0, -1)
              : currentHistory;

            return {
              ...prev,
              [targetLoopId]: [
                ...historyWithoutBuffer,
                stampTurn({
                  sender: 'ai' as const,
                  message: LOG_PREFIX + displayMessage,
                }),
              ],
            };
          }
        });
      }
    });

    SocketService.onHitlApprovalRequest((request: ToolApprovalRequest) => {
      // Do NOT clear isHitlTurnInFlight here — the orchestrator republishes
      // every still-pending interrupt on each streamed update while the
      // backend call is still actively running (mid-cascade), so this event
      // firing does not mean the call has returned. resume_turn_settled
      // below is the authoritative "safe to decide again" signal.
      // Added, not replaced — the model can dispatch several mutating calls
      // in parallel, each pausing as its own interrupt, so more than one can
      // be pending at once (keyed by tool_call_id).
      setHitlApprovals((prev) => ({
        ...prev,
        [request.tool_call_id]: request,
      }));
      hitlApprovalsRef.current = {
        ...hitlApprovalsRef.current,
        [request.tool_call_id]: request,
      }; // sync immediately for socket-event guards
      // A tool ask means the server took the plan decision that led here.
      pendingPlanSendRef.current = null;
      setPlanApproval(null); // mutually exclusive panels
      markPlanDecided('expired');
      // The matching 'tool' row was already inserted (running) by
      // tool_call_start before this ask fired — flip it in place so the
      // approval controls render inline in that same box. Fall back to
      // inserting a fresh row if no match is found (defensive: a missed
      // tool_call_start should not leave the request with no UI at all).
      setLocalChatHistories((prev) => {
        const loopId = selectedLoopIdRef.current || '';
        if (!loopId) return prev;
        const history = prev[loopId] || [];
        const hasMatch = history.some(
          (msg) =>
            msg.sender === 'tool' && msg.toolCallId === request.tool_call_id,
        );
        const updated = history.map((msg) =>
          msg.sender === 'tool' && msg.toolCallId === request.tool_call_id
            ? {
                ...msg,
                toolStatus: 'pending_approval' as const,
                wasGated: true as const,
              }
            : msg,
        );
        return {
          ...prev,
          [loopId]: hasMatch
            ? updated
            : [
                ...updated,
                stampTurn({
                  sender: 'tool' as const,
                  toolCallId: request.tool_call_id,
                  toolName: request.tool_name,
                  toolArgs: request.args || {},
                  toolStatus: 'pending_approval' as const,
                  startedAt: Date.now(),
                  wasGated: true as const,
                }),
              ],
        };
      });
    });

    // The backend's resume-turn call has fully returned — the one
    // unambiguous "safe to send the next HITL decision" signal, fired on
    // every exit path (clean finish, fresh re-interrupt, or error).
    SocketService.onResumeTurnSettled(() => {
      setIsHitlTurnInFlight(false);
    });

    SocketService.onPlanApprovalRequest((request: PlanApprovalRequest) => {
      // Discard any model text emitted before propose_plan: clear both the
      // streamingMessage buffer and the last SUMMARIZE_CHUNK entry that was
      // written directly into localChatHistories with isStreaming:true.
      setStreamingMessage('');
      setIsAIBuffering(false);
      isGeneratingRef.current = false;
      // Close out any still-pending plan first, so its history entry becomes
      // a decided (collapsed, read-only) record instead of a live card whose
      // buttons would act on the *new* plan below — e.g. on a reconnect
      // replay or a backend race that re-emits plan_approval_request before
      // the prior one was answered.
      if (pendingPlanCallIdRef.current) {
        markPlanDecided('expired');
      }
      const planCallId = generateUuid();
      pendingPlanCallIdRef.current = planCallId;
      setLocalChatHistories((prev) => {
        const loopId = selectedLoopIdRef.current || '';
        if (!loopId) return prev;
        const history = prev[loopId] || [];
        const lastMsg = history[history.length - 1];
        const trimmed = isTransientMessage(lastMsg)
          ? history.slice(0, -1)
          : history;
        // Rendered inline (like a tool call box) rather than as a panel
        // pinned above the input, so it scrolls with the rest of the turn
        // and remains a visible, re-expandable record after it's decided.
        return {
          ...prev,
          [loopId]: [
            ...trimmed,
            stampTurn({
              sender: 'plan_approval' as const,
              planCallId,
              plan: request,
              decision: 'pending' as const,
            }),
          ],
        };
      });
      planApprovalRef.current = request; // sync immediately for socket-event guards
      setPlanApproval(request);
      settleAllPendingHitl(); // mutually exclusive panels
    });

    // Receipt and terminal notice are appended inline so they scroll with the
    // rest of the turn, in the order the server sent them (receipt, then the
    // notice, then the assistant's closing text).
    SocketService.onExecutionReceipt((event: ExecutionReceiptEvent) => {
      // The loop the execution is running in, not the one being viewed: the user
      // may have switched loops while it ran, and the event carries no loop id.
      // Same expression chat_complete uses for the same reason (review of #1054).
      const loopId =
        processingLoopIdRef.current || selectedLoopIdRef.current || '';
      setLocalChatHistories((prev) => {
        if (!loopId || !event?.receipt) return prev;
        const history = prev[loopId] || [];
        // One receipt per execution: a repeated event replaces, never stacks.
        const withoutRepeat = history.filter(
          (m) =>
            !(
              m.sender === 'execution_receipt' &&
              m.planCallId === event.plan_call_id
            ),
        );
        const lastMsg = withoutRepeat[withoutRepeat.length - 1];
        const trimmed = isTransientMessage(lastMsg)
          ? withoutRepeat.slice(0, -1)
          : withoutRepeat;
        return {
          ...prev,
          [loopId]: [
            ...trimmed,
            stampTurn({
              sender: 'execution_receipt' as const,
              planCallId: event.plan_call_id,
              receipt: event.receipt,
              terminalStatus: event.terminal_status ?? null,
              live: true,
            }),
          ],
        };
      });
    });

    SocketService.onExecutionTerminal((event: ExecutionTerminalEvent) => {
      // The executing loop, as for the receipt above.
      const loopId =
        processingLoopIdRef.current || selectedLoopIdRef.current || '';
      setLocalChatHistories((prev) => {
        if (!loopId || !event) return prev;
        const history = prev[loopId] || [];
        const lastMsg = history[history.length - 1];
        const trimmed = isTransientMessage(lastMsg)
          ? history.slice(0, -1)
          : history;
        return {
          ...prev,
          [loopId]: [
            ...trimmed,
            stampTurn({
              sender: 'execution_terminal' as const,
              event,
              live: true,
            }),
          ],
        };
      });
    });

    SocketService.onIterationWarning((warning: IterationWarningEvent) => {
      setIterationWarning(warning);
      if (warning.level !== 'error') {
        setTimeout(() => {
          setIterationWarning((current) =>
            current === warning ? null : current,
          );
        }, 8000);
      }
    });

    SocketService.onClarificationNeeded((request: ClarificationRequest) => {
      setActiveClarification(request);
      setStreamingMessage('');
      setIsAIBuffering(false);
      const chatLoopId = selectedLoopId;
      setLocalChatHistories((prev) => {
        const currentHistory = prev[chatLoopId] || [];

        let messageText = '';

        if (request.clarification_text) {
          // Pre-rendered markdown from the LLM — pass through directly.
          messageText = request.clarification_text;
        } else if (
          // TODO: Remove this branch once all server versions send request.clarification_text.
          // Backward-compat fallback for older server versions that only send request.questions.
          request.questions &&
          Array.isArray(request.questions) &&
          request.questions.length > 0
        ) {
          const validQuestions = request.questions
            .map((q) => {
              if (q && typeof q === 'object' && q.question) return q.question;
              if (typeof q === 'string') return q;
              return null;
            })
            .filter(Boolean);

          messageText =
            validQuestions.length > 0
              ? validQuestions.join('\n')
              : t('clarification-needed');
        } else {
          messageText = t('clarification-needed');
        }

        const lastMsg = currentHistory[currentHistory.length - 1];
        if (
          lastMsg &&
          (lastMsg.sender === 'user' || lastMsg.sender === 'ai') &&
          lastMsg.message === messageText
        ) {
          return prev;
        }

        const historyWithoutBuffer = isTransientMessage(lastMsg)
          ? currentHistory.slice(0, -1)
          : currentHistory;

        return {
          ...prev,
          [chatLoopId]: [
            ...historyWithoutBuffer,
            stampTurn({
              sender: 'ai' as const,
              message: messageText,
            }),
          ],
        };
      });
    });

    aiService.onChatComplete((data: any) => {
      if (stopRequestedRef.current) {
        return;
      }
      isGeneratingRef.current = false;
      processingLoopIdRef.current = null;

      // Clear streaming message and add final response to chat history
      setStreamingMessage('');
      setIsAIBuffering(false);
      setIsHitlTurnInFlight(false);
      const currentLoopId = selectedLoopIdRef.current;
      const username = getCurrentUsername();
      const baseRoomId = `${_chatSessionId}:${username}`;
      // Single thread per conversation: backend room is session_id:username in
      // both chat and task mode (task_id no longer namespaces the room).
      if (data.room_id !== baseRoomId) {
        return;
      }

      const targetLoopId = currentLoopId;
      const rawMessage = data.message || t('processing-complete');
      // Strip the activation marker if the backend echoes a [[TASK_ACTIVATED]] turn
      const finalMessage = isActivationContent(rawMessage)
        ? stripActivationMarker(rawMessage)
        : formatAIResponse(rawMessage);

      setLocalChatHistories((prev) => {
        const currentHistory = prev[targetLoopId] || [];
        const lastMsg = currentHistory[currentHistory.length - 1];
        const historyWithoutBuffer = isTransientMessage(lastMsg)
          ? currentHistory.slice(0, -1)
          : currentHistory;

        return {
          ...prev,
          [targetLoopId]: [
            ...historyWithoutBuffer,
            stampTurn({
              sender: 'ai' as const,
              message: finalMessage,
            }),
          ],
        };
      });
      // Capture whether a plan/tool approval is currently awaiting the user's
      // response BEFORE clearing it — the refs hold the real current value
      // from the last synchronous assignment (not a stale closure snapshot).
      const hadActivePlan = planApprovalRef.current !== null;
      const hadActiveHitl = Object.keys(hitlApprovalsRef.current).length > 0;
      settleAllPendingHitl();
      setPlanApproval(null);
      planApprovalRef.current = null; // sync ref immediately
      if (hadActivePlan) markPlanDecided('expired');
      setIsStopping(false);
      setActiveClarification(null);
      setIsAIBuffering(false);
      // Only disconnect if no approval is still waiting for the user. A
      // spurious chat_complete while one is active would otherwise tear
      // down the socket before the user can approve/reject.
      if (!hadActivePlan && !hadActiveHitl) {
        SocketService.disconnect();
      }
    });

    aiService.onSocketError((error: any) => {
      if (stopRequestedRef.current) {
        return;
      }
      // A decision send raced another still-in-flight resume from the same
      // parallel batch (the backend allows only one turn per conversation
      // at a time) and was rejected outright — the backend never saw it.
      // This is expected and recoverable, not a fatal error: put the row
      // back to pending_approval so the user can retry once the current
      // cascade finishes, instead of tearing down the whole socket and
      // settling every other still-legitimately-in-flight row as failed.
      if (error?.code === 'turn_in_flight') {
        setIsHitlTurnInFlight(false);
        const toolCallId = error?.tool_call_id;
        const originalRequest = toolCallId
          ? pendingHitlSendCacheRef.current[toolCallId]
          : undefined;
        // Only take the benign path when we can actually restore the row —
        // an unrecognized/missing tool_call_id falls through to the generic
        // handling below rather than silently doing nothing.
        if (toolCallId && originalRequest) {
          delete pendingHitlSendCacheRef.current[toolCallId];
          restoreHitlRow(toolCallId, originalRequest);
          return;
        }
      }
      // The server refused an approval it could not verify against what it is
      // actually waiting on. Deliberately not the generic path below: the
      // interrupt is still pending, so tearing the socket down and settling
      // every row would lose the user's click and any retry. An unrecognised
      // refusal (no row or card to match) falls through to the generic handling.
      if (error?.code === 'approval_refused' && handleApprovalRefused(error)) {
        return;
      }
      // Break taint chain: reconstruct error as plain string (CWE-201)
      const safeErrorMsg = String(
        error?.message || error?.error || 'unknown',
      ).slice(0, 500);
      console.error('[Socket.IO] Error:', `${safeErrorMsg}`);

      isGeneratingRef.current = false;
      processingLoopIdRef.current = null;

      setStreamingMessage('');
      setIsAIBuffering(false);

      const errorMessage =
        error.details || error.error || t('an-error-occured');
      const chatLoopId = selectedLoopId;

      setLocalChatHistories((prev) => {
        const currentHistory = prev[chatLoopId] || [];

        // Only remove the last message if it's a buffer/streaming message
        // DON'T remove plan messages or other completed messages
        const lastMsg = currentHistory[currentHistory.length - 1];
        const isBufferMessage =
          lastMsg &&
          lastMsg.sender === 'ai' &&
          (lastMsg.message === t('ai-buffer-msg') ||
            lastMsg.message.startsWith(t('processing-ai')) ||
            lastMsg.message.startsWith(t('using-tool')) ||
            lastMsg.message.startsWith(t('validating')));

        const newHistory = isBufferMessage
          ? currentHistory.slice(0, -1)
          : currentHistory;

        return {
          ...prev,
          [chatLoopId]: [
            ...newHistory,
            stampTurn({
              sender: 'ai' as const,
              message: `${t('error-prefix')}${errorMessage}`,
            }),
          ],
        };
      });
      settleAllPendingHitl();
      setPlanApproval(null);
      markPlanDecided('expired');
      setActiveClarification(null);
      SocketService.disconnect();
    });

    SocketService.markListenersBound();
  };

  // Update chat history when streaming message changes
  useEffect(() => {
    if (!isGeneratingRef.current && streamingMessage) {
      return;
    }
    if (streamingMessage && selectedLoopId) {
      const chatLoopId = selectedLoopId;
      setLocalChatHistories((prev) => {
        const currentHistory = prev[chatLoopId] || [];
        const lastMsg = currentHistory[currentHistory.length - 1];

        // Only update if the last message is a buffer/streaming message
        // Don't replace plan messages or completed messages
        if (isTransientMessage(lastMsg)) {
          return {
            ...prev,
            [chatLoopId]: [
              ...currentHistory.slice(0, -1), // Remove old stream chunk
              stampTurn({
                sender: 'ai' as const,
                message: streamingMessage, // Add new stream chunk
              }),
            ],
          };
        } else {
          return {
            ...prev,
            [chatLoopId]: [
              ...currentHistory,
              stampTurn({
                sender: 'ai' as const,
                message: streamingMessage,
              }),
            ],
          };
        }
      });
    }
  }, [streamingMessage]);

  useEffect(() => {
    // Handler for clicks outside components
    const handleClickOutside = (event: MouseEvent) => {
      // Get the clicked element
      const clickedElement = event.target as Node;

      // Close tool suggestions when clicking outside
      if (showToolSuggestions) {
        const inputContainer = document.getElementById('id-ai-text-input');
        if (inputContainer && !inputContainer.contains(clickedElement)) {
          setTimeout(() => {
            setShowToolSuggestions(false);
          }, 100);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showToolSuggestions]);

  const handleClose = () => {
    setSelectedPrompt(-1);
    setNoLoopWarning('');
    setIsAIBuffering(false);
    setShowClearCommand(false);
    setSelectedTaskStarters(null);
    lastTaskIdSentRef.current = null;
    lastTaskIconRef.current = null;
    lastTaskNameRef.current = null;
    setActiveTaskInfo(null);
    setContextWindow(null);
    // Clear pending clarification/approval so reopening doesn't carry stale state
    setActiveClarification(null);
    settleAllPendingHitl();
    setPlanApproval(null);
    markPlanDecided('expired');
    setIterationWarning(null);
    // Clear any cached AI task definitions in sessionStorage so reopening re-fetches
    try {
      const prefix = 'ai:tasks:v1:';
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(prefix)) {
          sessionStorage.removeItem(key);
        }
      }
    } catch (_) {
      // Ignore storage errors silently
    }
    onClose();
  };

  const handleLoopSelection = (id: string) => {
    const loop = availableLoops.find((loop) => loop.id === id);
    setSelectedLoopId(id);
    setSelectedLoopName(loop ? loop.name : null);
    setNoLoopWarning('');
    setSelectedTaskStarters(null);
    lastTaskIdSentRef.current = null;
    lastTaskIconRef.current = null;
    lastTaskNameRef.current = null;
    setActiveTaskInfo(null);
  };

  const updateChatHistories = (newHistories: any) => {
    localChatHistoriesRef.current = newHistories;
    setLocalChatHistories(newHistories);
    setChatHistories(newHistories);
  };

  // Append a "switched to Task" divider WITHOUT clearing existing messages,
  // so the read-only conversation carries into the Task (single backend thread).
  const appendTaskDivider = (
    loopIdToAppend: string,
    dividerMessage: {
      sender: 'ai';
      message: string;
      subMessage?: React.ReactNode;
      taskMeta?: {
        taskId?: string;
        taskName: string;
        taskDescription?: string;
      };
    },
  ) => {
    const prev = localChatHistoriesRef.current[loopIdToAppend] || [];
    updateChatHistories({
      ...localChatHistoriesRef.current,
      [loopIdToAppend]: [...prev, dividerMessage],
    });
  };

  // Optimistically clear chat history locally and optionally fire backend clear.
  // Optionally seed the cleared chat with an initial message (e.g., task intro).
  // Set deleteFromBackend=true only when the user explicitly wants to delete the
  // conversation (e.g. /clear command). New-chat and task-switch callers should
  // leave it false so the old session is preserved in history.
  const clearChatOptimistic = (
    loopIdToClear: string,
    initialMessage?: {
      sender: 'user' | 'ai';
      message: string;
      subMessage?: React.ReactNode;
    },
    regenerateSessionId: boolean = false,
    deleteFromBackend: boolean = false,
  ) => {
    const updatedHistories = {
      ...localChatHistories,
      [loopIdToClear]: initialMessage ? [initialMessage] : [],
    };
    updateChatHistories(updatedHistories);

    setSearchContexts((prev) => ({
      ...prev,
      [loopIdToClear]: {
        results: { hits: [] },
        renderedResults: null,
        searchContext: '',
        hasSearched: false,
      },
    }));

    setShowClearCommand(false);
    setInputMessage('');
    setSelectedPrompt(-1);
    setNoLoopWarning('');

    if (deleteFromBackend) {
      const username =
        userRef.current?.profile?.preferred_username ||
        userRef.current?.profile?.email?.split('@')[0] ||
        'user';

      // Fire-and-forget backend clear; suppress any errors
      aiService
        .clearChat(_chatSessionId, username, teamspaceId ?? '')
        .catch(() => {});
    }

    // Regenerate session ID on explicit user actions (New Chat, Clear)
    // In task mode, new session_id is already generated in handleTaskSendToChat
    if (regenerateSessionId) {
      _chatSessionId = generateUuid();
      bumpSessionIdVersion((v) => v + 1);
    }
  };

  const handleSendMessage = async (
    overrideText?: string,
    taskId?: string,
    skipClarification?: boolean,
  ) => {
    stopRequestedRef.current = false;
    if (isIngestionActive) {
      return;
    }
    if (selectedTaskStarters) setSelectedTaskStarters(null);
    const messageInput = (overrideText ?? inputMessage).trim();
    // only check for empty message if no tool is selected
    if (!messageInput && !selectedTool) {
      setNoLoopWarning(t('message-cannot-be-empty'));
      return;
    }

    // Determine the active loop context for this message. When opened from the
    // global AI button, selectedLoopId may be set via the welcome card; when
    // opened from a specific Loop card, loopId prop is provided. Prefer the
    // explicit selection, but fall back to the prop if needed.
    const chatLoopId = (selectedLoopId || loopId || '').trim();
    const chatLoopName = selectedLoopName;

    if (!chatLoopId) {
      setNoLoopWarning(t('no-loop-selected'));
      return;
    }
    const isSearchPrompt = selectedPrompt === 0;
    const isAskQuestionPrompt = selectedPrompt === 1;
    const searchMessage = isSearchPrompt
      ? t('ai-search-prompt') + ' ' + messageInput
      : '';

    // Create a formatted message that shows the tool selection nicely in the chat history
    let formattedMessage = messageInput;
    if (selectedTool && !formattedMessage.startsWith('#')) {
      formattedMessage = `#${selectedTool} ${formattedMessage}`;
    }

    // A fresh turn starts here — every message from this point on (this
    // user message, the agent's reasoning and answer, and any tool calls)
    // carries this id via stampTurn(), until the next call mints a new one.
    currentTurnIdRef.current = generateUuid();
    const latestHistories = localChatHistoriesRef.current;
    const updatedChatHistories = {
      ...latestHistories,
      [chatLoopId]: [
        ...(latestHistories[chatLoopId] || []),
        stampTurn({
          sender: 'user' as const,
          message: searchMessage ? searchMessage : formattedMessage,
        }),
      ],
    };
    updateChatHistories(updatedChatHistories);
    setInputMessage('');
    setNoLoopWarning('');

    if (activeClarification && !skipClarification) {
      try {
        setIsAIBuffering(true);
        // Pass token for LangGraph interrupt-based resume
        const userToken = userRef.current?.access_token || '';
        SocketService.sendClarificationResponse(
          messageInput,
          _chatSessionId,
          userToken,
          lastTaskIdSentRef.current || undefined,
        );

        setActiveClarification(null);
      } catch (error) {
        console.error('Failed to send clarification response', error);
        setIsAIBuffering(false);
      }
      return;
    }

    // Socket.IO Path - Check if Socket.IO is enabled
    if (settings.useSocketIO) {
      try {
        isGeneratingRef.current = true;
        setIsAIBuffering(true);
        processingLoopIdRef.current = chatLoopId;

        const messageForAI = messageInput;

        // Check if a tool is specified
        let specificTool: string | undefined = undefined;
        const hashtagRegex = /#(\w+)/g;
        const hashtagMatches = Array.from(messageForAI.matchAll(hashtagRegex));

        if (hashtagMatches.length > 0) {
          specificTool = hashtagMatches[0][1];
        } else if (selectedTool) {
          specificTool = selectedTool;
        }

        if (specificTool === '' || !specificTool) {
          specificTool = undefined;
        }

        // Get user token
        const userToken = userRef.current?.access_token || '';

        if (!SocketService.isConnected()) SocketService.disconnect();
        await aiService.connectSocketIO(userToken);
        initializeSocketListeners();

        // Send message via Socket.IO — reset HITL state for the new turn
        settleAllPendingHitl();
        setPlanApproval(null);
        markPlanDecided('expired');
        setIterationWarning(null);
        setIsStopping(false);

        aiService.sendMessageSocketIO(
          chatLoopId,
          chatLoopName,
          messageForAI,
          teamspaceId || '',
          teamspaceName || '',
          userToken,
          specificTool,
          undefined,
          taskId,
          _chatSessionId,
          chatLoopId,
          tenantId || '',
          lastTaskIconRef.current || undefined,
          lastTaskNameRef.current || undefined,
        );

        // Response will be handled by Socket.IO event listeners
        // The streaming updates will automatically update the UI
      } catch (error: any) {
        console.error('[Socket.IO] Error sending message:', error);
        setIsAIBuffering(false);

        const errorMessage = error.message || t('failed-to-send-mssg-socket');
        const errorHistories = {
          ...updatedChatHistories,
          [chatLoopId]: [
            ...updatedChatHistories[chatLoopId],
            stampTurn({
              sender: 'ai' as const,
              message: `${t('error-prefix')}${errorMessage}`,
            }),
          ],
        };
        updateChatHistories(errorHistories);
        setIsAIBuffering(false);
      }

      return; // Exit early for Socket.IO path
    }

    // REST Path - Original implementation
    try {
      setIsAIBuffering(true);
      const thinkingHistories = {
        ...updatedChatHistories,
        [chatLoopId]: [
          ...(updatedChatHistories[chatLoopId] || []),
          stampTurn({ sender: 'ai' as const, message: t('ai-buffer-msg') }),
        ],
      };
      updateChatHistories(thinkingHistories);

      const messageForAI = messageInput;

      try {
        const promptId = isAskQuestionPrompt ? 'ask-a-question' : undefined;

        // Check if a tool is specified in the message using hashtag pattern #toolname
        let specificTool: string | undefined = undefined;
        const hashtagRegex = /#(\w+)/g;
        const hashtagMatches = Array.from(messageForAI.matchAll(hashtagRegex));

        if (hashtagMatches.length > 0) {
          // Extract the tool name from the first hashtag match
          specificTool = hashtagMatches[0][1];
        } else if (selectedTool) {
          // Use the explicitly selected tool if no hashtag is found in the message
          specificTool = selectedTool;
        }

        // Make sure the tool name is valid
        if (specificTool === '' || !specificTool) {
          specificTool = undefined;
        }

        const aiServiceResp = await aiService.sendMessage(
          chatLoopId,
          chatLoopName,
          messageForAI,
          teamspaceId,
          promptId,
          specificTool,
          undefined,
          teamspaceName,
          taskId,
          _chatSessionId,
          loopId,
        );

        // If user stopped while waiting for response, abort further updates
        if (stopRequestedRef.current) {
          setIsAIBuffering(false);
          setSelectedTool(null);
          return;
        }

        if (!aiServiceResp.success && aiServiceResp.error) {
          const errorInfo = extractErrorInfo(aiServiceResp);
          const errorMessage = handleGeneralError(errorInfo, t);

          // Update chat history with error message
          const errorHistories = {
            ...thinkingHistories,
            [chatLoopId]: [
              ...thinkingHistories[chatLoopId].slice(0, -1),
              stampTurn({
                sender: 'ai' as const,
                message: errorMessage,
              }),
            ],
          };
          updateChatHistories(errorHistories);
        } else if (aiServiceResp?.message) {
          const finalAIHistories = {
            ...thinkingHistories,
            [chatLoopId]: [
              ...thinkingHistories[chatLoopId].slice(0, -1),
              stampTurn({
                sender: 'ai' as const,
                message: aiServiceResp?.message,
                //subMessage: helpPrompts,
              }),
            ],
          };
          updateChatHistories(finalAIHistories);
          const nextConversationId =
            aiServiceResp?.base_conversation_id ??
            aiServiceResp?.conversation_id;

          if (
            nextConversationId &&
            nextConversationId !== chatLoopId &&
            nextConversationId !== 'default' &&
            isSafeKey(nextConversationId)
          ) {
            // Migrate chat history from old key to new key so the UI
            // continues to display the conversation under the updated id.
            const migratedHistories = { ...finalAIHistories };
            safeAssignKey(
              migratedHistories,
              nextConversationId,
              migratedHistories[chatLoopId],
            );
            delete migratedHistories[chatLoopId];
            updateChatHistories(migratedHistories);

            const loop = availableLoops.find(
              (loop) => loop.id === nextConversationId,
            );
            setSelectedLoopId(nextConversationId);
            setSelectedLoopName(loop ? loop.name : chatLoopName);

            // Keep session ID in sync with the backend-assigned conversation ID
            _chatSessionId = nextConversationId;
            bumpSessionIdVersion((v) => v + 1);
          }
        } else {
          // Remove thinking message and add error
          const errorHistories = {
            ...thinkingHistories,
            [chatLoopId]: [
              ...thinkingHistories[chatLoopId].slice(0, -1),
              stampTurn({
                sender: 'ai' as const,
                message: t('ai-service-error'),
              }),
            ],
          };
          updateChatHistories(errorHistories);
        }
      } catch (aiError: any) {
        if (stopRequestedRef.current) {
          setIsAIBuffering(false);
          setSelectedTool(null);
          return;
        }
        let errorMessage = t('ai-service-unavailable');

        try {
          // Direct handling for backend error response structure
          if (aiError.response && aiError.response.data) {
            errorMessage = handleBackendErrorResponse(aiError.response.data, t);
          } else {
            // Fallback to our helper function for other error formats
            const errorData =
              aiError.response?.data || aiError.data || aiError.error;
            const errorInfo = extractErrorInfo(errorData);
            errorMessage = handleGeneralError(errorInfo, t);
          }
        } catch (e) {
          console.error('Error handling AI error response:', e);
        }

        const errorHistories = {
          ...thinkingHistories,
          [chatLoopId]: [
            ...thinkingHistories[chatLoopId].slice(0, -1),
            stampTurn({
              sender: 'ai' as const,
              message: errorMessage,
            }),
          ],
        };
        updateChatHistories(errorHistories);
      }
    } catch (error) {
      const errorHistories = {
        ...updatedChatHistories,
        [chatLoopId]: [
          ...updatedChatHistories[chatLoopId],
          stampTurn({
            sender: 'ai' as const,
            message: t('server-error'),
            //subMessage: helpPrompts,
          }),
        ],
      };
      updateChatHistories(errorHistories);
    } finally {
      // Always set buffering state to false when done
      setIsAIBuffering(false);
      setSelectedTool(null);
    }
    setSelectedPrompt(-1);
  };

  const handleStopExecution = () => {
    stopRequestedRef.current = true;
    setIsAIBuffering(false);
  };

  const handleAbortAndStop = () => {
    if (isStopping) return;
    try {
      SocketService.abortTask();
      setIsStopping(true);
    } catch (err) {
      console.error('[AIModule] abortTask failed', err);
    }
    if (services?.stopPrompt?.show) {
      handleStopExecution();
    }
  };

  const handleSendMessageWithTool = (
    overrideText?: string,
    taskId?: string,
    skipClarification?: boolean,
  ) => {
    if (selectedTaskStarters) setSelectedTaskStarters(null);
    const baseText = (overrideText ?? inputMessage).trim();
    if (!baseText && !selectedTool) {
      setNoLoopWarning(t('message-cannot-be-empty'));
      return;
    }

    // Use the active task ID if no explicit taskId is provided
    const effectiveTaskId = taskId || lastTaskIdSentRef.current || undefined;

    // If a tool is selected, modify the message to include the tool hashtag
    if (selectedTool) {
      const composed = `#${selectedTool} ${baseText}`;
      setSelectedTool(null);
      handleSendMessage(composed, effectiveTaskId, skipClarification);
    } else {
      handleSendMessage(baseText, effectiveTaskId, skipClarification);
    }
  };

  const handleSaveChats = () => {
    if (!selectedLoopId) {
      setNoLoopWarning(t('no-loop-to-save-chats'));
      return;
    }

    const chatLoopId = selectedLoopId;
    if (!localChatHistories[chatLoopId]) {
      setNoLoopWarning(t('no-loop-to-save-chats'));
      return;
    }

    const chatData = localChatHistories[chatLoopId] || [];
    const cleanData = sanitizeChatForDownload(chatData, LOG_PREFIX);
    const jsonData = JSON.stringify(cleanData, null, 2);

    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    // Validate the generated URL is a safe blob: scheme before navigating
    if (!url.startsWith('blob:')) {
      console.error('Unexpected URL scheme from createObjectURL');
      return;
    }

    const safeName = (selectedLoopName || t('chat-history')).replace(
      /[/\\:\0]/g,
      '_',
    );
    const link = document.createElement('a');
    // Break taint chain: reconstruct validated blob URL string (CWE-601)
    link.href = String(url);
    link.download = `${safeName}.json`;
    link.click();

    URL.revokeObjectURL(url);
    setNoLoopWarning('');
  };

  const handleVoiceInput = async () => {
    // Prevent voice input when AI is processing or buffering
    if (isAIBuffering) {
      return;
    }

    // If already listening, stop the recognition
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }

    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setNoLoopWarning(t('speech-recognition-not-supported'));
      return;
    }

    // Request microphone permission explicitly before starting recognition
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Stop the stream immediately; we only needed it to trigger the permission prompt
      stream.getTracks().forEach((track) => track.stop());
    } catch (err) {
      console.error('Microphone permission denied:', err);
      setNoLoopWarning(
        `${t('speech-recognition-error')}: ${t('microphone-permission-denied') || 'Microphone permission denied'}`,
      );
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event: any) => {
      try {
        let interimText = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;

          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimText += transcript;
          }
        }

        // Update interim transcript for live display
        interimTranscriptRef.current = interimText;
        setInterimTranscript(interimText);

        // Append final transcript to input message with proper spacing
        if (finalTranscript) {
          setInputMessage((prev) => {
            // Add space before final transcript if previous content doesn't end with space
            if (prev && !prev.endsWith(' ')) {
              return prev + ' ' + finalTranscript;
            }
            return prev + finalTranscript;
          });
        }
      } catch (error) {
        console.error('Error processing speech recognition result:', error);
      }
    };

    recognition.onerror = (event: any) => {
      const safeError = String(event.error || 'unknown').slice(0, 200);
      // Break taint chain: reconstruct error string to sever lineage (CWE-201)
      console.error(t('speech-recognition-error'), `${safeError}`);
      setNoLoopWarning(`${t('speech-recognition-error')}: ${safeError}`);
    };

    recognition.onend = () => {
      setIsListening(false);
      // Commit any remaining interim text to input message before clearing
      const interim = interimTranscriptRef.current;
      if (interim) {
        setInputMessage((msg) => {
          // Add space if needed before committing interim text
          if (msg && !msg.endsWith(' ')) {
            return msg + ' ' + interim;
          }
          return msg + interim;
        });
      }
      interimTranscriptRef.current = '';
      setInterimTranscript('');
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  // Stops/cancels the current in-flight AI operation (Socket.IO or REST)
  // Behavior: no extra chat message; keep any partial AI response; only remove transient buffer/status lines

  const handleTaskSendToChat = (taskId: string, text: string) => {
    // Never wipe pending clarification/approval state or drop the socket
    // while ingestion is active — the backend would reject the resulting
    // resume/chat request anyway, so wiping state here just strands the
    // user with nothing left to act on.
    //
    // Read via the ref (not the closed-over `isIngestionActive` variable):
    // this function instance can be called from a stale closure — e.g. a
    // task-starter button persisted into chat history by appendTaskDivider —
    // that was frozen at a render before ingestion started. The ref's
    // `.current` is kept live by the effect above, so even that stale
    // closure observes ingestion state as of the click.
    if (isIngestionActiveRef.current) {
      return;
    }
    if (!text) return;

    setActiveTab('chats');
    if (selectedTaskStarters) setSelectedTaskStarters(null);

    // Continuity: switching to a (different) task no longer starts a fresh
    // conversation — the backend keeps one thread. We only need to clear a
    // stale clarification/approval and drop the socket so this goes out as a
    // fresh chat_request (not a resume). Session id and messages are kept.
    const hasStalePause =
      activeClarification !== null ||
      Object.keys(hitlApprovalsRef.current).length > 0 ||
      planApproval !== null;
    if (hasStalePause) {
      setActiveClarification(null);
      settleAllPendingHitl();
      setPlanApproval(null);
      markPlanDecided('expired');
      setIterationWarning(null);
      SocketService.disconnect();
    }
    lastTaskIdSentRef.current = taskId;

    if (!isAIBuffering) {
      handleSendMessageWithTool(text, taskId, false);
    }
  };

  // --- Plan approval handlers ---
  // Marks the persisted 'plan_approval' history entry as decided so it
  // renders as a collapsed, read-only record instead of the live card.
  const markPlanDecided = (
    decision: 'approved' | 'rejected' | 'changes_requested' | 'expired',
  ) => {
    const callId = pendingPlanCallIdRef.current;
    if (!callId) return;
    setLocalChatHistories((prev) => {
      const loopId = selectedLoopIdRef.current || '';
      if (!loopId) return prev;
      const history = prev[loopId] || [];
      return {
        ...prev,
        [loopId]: history.map((msg) =>
          msg.sender === 'plan_approval' && msg.planCallId === callId
            ? { ...msg, decision }
            : msg,
        ),
      };
    });
    pendingPlanCallIdRef.current = null;
  };

  // --- Tool approval (HITL) row settlement ---
  // Updates the inline 'tool' history entry (matched by toolCallId) that a
  // tool_call_start already inserted before the ask fired — the pending
  // approval renders as that same ToolCallBox row, not a separate panel, so
  // deciding or abandoning it must update that row rather than a floating
  // state object.
  const markHitlRowSettled = (
    toolCallId: string,
    status: 'running' | 'success' | 'error',
    result?: string,
    // Only the two real decision call sites in onHitlDecide pass this.
    // settleAllPendingHitl (abandonment — turn superseded, socket error,
    // panel closed) settles a row without a decision ever having been
    // made, and must not stamp one.
    gateDecision?: 'approved' | 'rejected',
    // Defaults to selectedLoopIdRef.current, the currently-*viewed* loop —
    // correct for every existing caller, all of which act on the user's own
    // live decision on whatever they're looking at. A caller settling a
    // *different*, no-longer-viewed loop (the disconnect handler below,
    // which already computed its own targetLoopId) must pass it explicitly,
    // or this silently no-ops there while acting on the wrong loop instead
    // (found in review of #989).
    explicitLoopId?: string,
  ) => {
    setLocalChatHistories((prev) => {
      const loopId = explicitLoopId ?? (selectedLoopIdRef.current || '');
      if (!loopId) return prev;
      return {
        ...prev,
        [loopId]: (prev[loopId] || []).map((msg) => {
          if (msg.sender !== 'tool' || msg.toolCallId !== toolCallId) {
            return msg;
          }
          // Abandonment (settleAllPendingHitl, no gateDecision passed) must
          // never downgrade a call that already reached a terminal state.
          // The real fix is removing a resolved call from hitlApprovals the
          // moment tool_call_result arrives (see that handler); this is the
          // second layer, so a stale/reintroduced map entry can't resurrect
          // the same false-failure bug through this function specifically.
          // A real decision (gateDecision set) always applies — it targets
          // a row that is, by construction, still pending_approval.
          if (
            gateDecision === undefined &&
            (msg.toolStatus === 'success' || msg.toolStatus === 'error')
          ) {
            return msg;
          }
          return {
            ...msg,
            toolStatus: status,
            ...(result !== undefined ? { toolResult: result } : {}),
            ...(gateDecision ? { gateDecision } : {}),
          };
        }),
      };
    });
  };

  // Settles every currently-pending tool-approval row (used on abandonment:
  // chat_complete without a decision, socket error, task switch, closing the
  // panel, a new plan/turn superseding it) — a single-row settle would leave
  // the other rows stuck showing live Approve/Reject buttons for interrupts
  // that are no longer answerable.
  const settleAllPendingHitl = (explicitLoopId?: string) => {
    Object.keys(hitlApprovalsRef.current).forEach((toolCallId) => {
      markHitlRowSettled(
        toolCallId,
        'error',
        'No longer pending.',
        undefined,
        explicitLoopId,
      );
    });
    hitlApprovalsRef.current = {};
    setHitlApprovals({});
    pendingHitlSendCacheRef.current = {};
    pendingPlanSendRef.current = null;
    setIsHitlTurnInFlight(false);
  };

  // Puts a tool-approval row back to a decidable state after a decision that
  // never took effect (the server rejected the send, or refused to verify it).
  // Undoes every field the optimistic approve/reject set in onHitlDecide: the
  // status, the decision stamp and a "Rejected by user." result.
  const restoreHitlRow = (
    toolCallId: string,
    original: ToolApprovalRequest,
  ) => {
    setIsHitlTurnInFlight(false);
    setHitlApprovals((prev) => ({ ...prev, [toolCallId]: original }));
    hitlApprovalsRef.current = {
      ...hitlApprovalsRef.current,
      [toolCallId]: original,
    };
    setLocalChatHistories((prev) => {
      const loopId = selectedLoopIdRef.current || '';
      if (!loopId) return prev;
      return {
        ...prev,
        [loopId]: (prev[loopId] || []).map((msg) =>
          msg.sender === 'tool' && msg.toolCallId === toolCallId
            ? {
                ...msg,
                toolStatus: 'pending_approval' as const,
                toolResult: undefined,
                // S3b hides a row from the column on gateDecision alone, so
                // it must go too or the restored row stays hidden.
                gateDecision: undefined,
              }
            : msg,
        ),
      };
    });
  };

  // An approval the server refused (code 'approval_refused', see
  // Orchestrator._refuse_resume). The server leaves the interrupt pending and
  // says whether answering again can work (`retryable`), and echoes the ids the
  // client sent. Retryable: the card or row comes back so the user can click
  // again. Otherwise: the card or row is settled as no longer pending, the
  // reason is shown, and nothing else is touched (no disconnect, and other
  // pending rows of a parallel batch stay answerable).
  // Returns false when no pending decision matches, so the caller falls back to
  // the generic error handling.
  const handleApprovalRefused = (error: any): boolean => {
    const retryable = error?.retryable === true;
    const toolCallId =
      typeof error?.tool_call_id === 'string' ? error.tool_call_id : '';
    const planCallId =
      typeof error?.plan_call_id === 'string' ? error.plan_call_id : '';
    const toolCache = pendingHitlSendCacheRef.current;
    const planSend = pendingPlanSendRef.current;

    // Which decision was refused: the echoed id first; with no id at all, the
    // single decision in flight.
    let refusedToolId = '';
    let refusedPlan: typeof planSend = null;
    if (toolCallId && toolCache[toolCallId]) {
      refusedToolId = toolCallId;
    } else if (
      planCallId &&
      planSend &&
      planSend.request.plan_call_id === planCallId
    ) {
      refusedPlan = planSend;
    } else if (!toolCallId && !planCallId) {
      const cached = Object.keys(toolCache);
      if (cached.length === 1 && !planSend) refusedToolId = cached[0];
      else if (cached.length === 0 && planSend) refusedPlan = planSend;
    }
    if (!refusedToolId && !refusedPlan) return false;

    setIsHitlTurnInFlight(false);
    if (refusedToolId) {
      const original = toolCache[refusedToolId];
      delete toolCache[refusedToolId];
      if (retryable) {
        restoreHitlRow(refusedToolId, original);
      } else {
        setLocalChatHistories((prev) => {
          const loopId = selectedLoopIdRef.current || '';
          if (!loopId) return prev;
          return {
            ...prev,
            [loopId]: (prev[loopId] || []).map((msg) =>
              msg.sender === 'tool' && msg.toolCallId === refusedToolId
                ? {
                    ...msg,
                    toolStatus: 'error' as const,
                    toolResult: 'No longer pending.',
                    gateDecision: undefined,
                  }
                : msg,
            ),
          };
        });
      }
    }
    if (refusedPlan) {
      pendingPlanSendRef.current = null;
      // The plan handlers marked the turn as running; the refusal ran nothing.
      isGeneratingRef.current = false;
      processingLoopIdRef.current = null;
      setIsAIBuffering(false);
      if (retryable) {
        planApprovalRef.current = refusedPlan.request;
        pendingPlanCallIdRef.current = refusedPlan.cardId;
        setPlanApproval(refusedPlan.request);
      }
      const cardId = refusedPlan.cardId;
      if (cardId) {
        setLocalChatHistories((prev) => {
          const loopId = selectedLoopIdRef.current || '';
          if (!loopId) return prev;
          return {
            ...prev,
            [loopId]: (prev[loopId] || []).map((msg) =>
              msg.sender === 'plan_approval' && msg.planCallId === cardId
                ? {
                    ...msg,
                    decision: retryable
                      ? ('pending' as const)
                      : ('expired' as const),
                  }
                : msg,
            ),
          };
        });
      }
    }

    // Say why, in the same plain error line the generic handler uses.
    const reason = String(
      error?.details || error?.error || t('an-error-occured'),
    ).slice(0, 500);
    setLocalChatHistories((prev) => {
      const loopId = selectedLoopIdRef.current || '';
      if (!loopId) return prev;
      const history = prev[loopId] || [];
      const trimmed = isTransientMessage(history[history.length - 1])
        ? history.slice(0, -1)
        : history;
      return {
        ...prev,
        [loopId]: [
          ...trimmed,
          stampTurn({
            sender: 'ai' as const,
            message: `${t('error-prefix')}${reason}`,
          }),
        ],
      };
    });
    return true;
  };

  // Names which pending plan a plan_approval_response answers. Omitted when
  // the request did not carry an id (a server that predates it), in which case
  // the server accepts the answer only if exactly one plan is pending.
  const planCallIdField = () =>
    planApproval?.plan_call_id
      ? { plan_call_id: planApproval.plan_call_id }
      : {};

  const handlePlanApprove = () => {
    // No scope is sent: the server approves the domains of the plan it stored,
    // which are the domains of the rows the user is looking at.
    try {
      const userToken = userRef.current?.access_token || '';
      SocketService.resumeConversation(
        {
          type: 'plan_approval_response',
          approved: true,
          ...planCallIdField(),
        },
        userToken,
      );
      // The backend keeps running the resumed turn after this emit (same as
      // any other message send) — restore the "turn in progress" markers so
      // the wave dots / Stop button don't drop back to idle while it works.
      isGeneratingRef.current = true;
      processingLoopIdRef.current = selectedLoopIdRef.current;
      setIsAIBuffering(true);
      // Dismiss AFTER a successful emit so the card stays visible if the
      // socket call throws (user can retry rather than silently losing the card).
      pendingPlanSendRef.current = planApproval
        ? { request: planApproval, cardId: pendingPlanCallIdRef.current }
        : null;
      setPlanApproval(null);
      planApprovalRef.current = null;
      markPlanDecided('approved');
    } catch (err) {
      console.error('[AIModule] plan approve failed', err);
    }
  };

  const handlePlanReject = () => {
    try {
      const userToken = userRef.current?.access_token || '';
      SocketService.resumeConversation(
        {
          type: 'plan_approval_response',
          approved: false,
          ...planCallIdField(),
        },
        userToken,
      );
      // Rejection still resumes the LangGraph interrupt — the agent keeps
      // running (e.g. to acknowledge the rejection) — so the same busy state
      // applies here too.
      isGeneratingRef.current = true;
      processingLoopIdRef.current = selectedLoopIdRef.current;
      setIsAIBuffering(true);
      pendingPlanSendRef.current = planApproval
        ? { request: planApproval, cardId: pendingPlanCallIdRef.current }
        : null;
      setPlanApproval(null);
      planApprovalRef.current = null;
      markPlanDecided('rejected');
    } catch (err) {
      console.error('[AIModule] plan reject failed', err);
    }
  };

  const handlePlanRequestChanges = (feedback: string) => {
    try {
      const userToken = userRef.current?.access_token || '';
      SocketService.resumeConversation(
        {
          type: 'plan_approval_response',
          approved: false,
          feedback,
          ...planCallIdField(),
        },
        userToken,
      );
      isGeneratingRef.current = true;
      processingLoopIdRef.current = selectedLoopIdRef.current;
      setIsAIBuffering(true);
      pendingPlanSendRef.current = planApproval
        ? { request: planApproval, cardId: pendingPlanCallIdRef.current }
        : null;
      setPlanApproval(null);
      planApprovalRef.current = null;
      markPlanDecided('changes_requested');
    } catch (err) {
      console.error('[AIModule] plan request-changes failed', err);
    }
  };

  if (!isVisible) return null;

  const currentChatHistory = selectedLoopId
    ? localChatHistories[selectedLoopId] || []
    : [];

  const showWelcome = selectedLoopId ? false : true;

  const displayValue =
    inputMessage +
    (interimTranscript && inputMessage && !inputMessage.endsWith(' ')
      ? ' '
      : '') +
    interimTranscript;

  const getCurrentUserDisplayName = () => {
    return (
      user?.profile?.name ||
      user?.profile?.preferred_username ||
      user?.profile?.email?.split('@')[0] ||
      t('user-default-name')
    );
  };

  return (
    <div
      id="id-ai-module-container"
      data-testid="ai-module-overlay"
      style={{
        ...cardStyles.aiModuleOverlayFixed,
        top: `${48 + announcementOffset}px`,
        height: `calc(100vh - ${48 + announcementOffset}px)`,
      }}
    >
      <div
        id="id-ai-module-column"
        data-testid="ai-module-column"
        style={{
          ...cardStyles.aiModuleContainerColumn,
          width: `${panelWidthPercent}%`,
          backgroundColor: darkMode ? '#1e1e1e' : '#fff',
          color: darkMode ? '#e0e0e0' : '#161616',
        }}
      >
        {/* Drag handle for resizing */}
        <button
          type="button"
          aria-label="Resize AI panel"
          data-testid="ai-module-resize-handle"
          onMouseDown={handleResizeMouseDown}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '5px',
            height: '100%',
            cursor: 'ew-resize',
            zIndex: 1202,
            background: 'transparent',
            border: 'none',
            padding: 0,
          }}
        />
        <ChatHeader
          darkMode={darkMode}
          selectedLoopName={selectedLoopName}
          isAIBuffering={isAIBuffering}
          selectedLoopId={selectedLoopId}
          activeTab={activeTab}
          onTabChange={(v) => {
            if ((v === 'tasks' || v === 'history') && !selectedLoopId) return;
            setActiveTab(v as 'tasks' | 'chats' | 'history' | 'mcp');
          }}
          onNewChat={handleNewChat}
          onDownload={handleSaveChats}
          onClose={handleClose}
          threadId={_chatSessionId}
          tracingEnabled={tracingEnabled}
          activeTaskInfo={activeTaskInfo}
          hasChatHistory={currentChatHistory.length > 0}
        />
        {activeTab === 'chats' && (
          <>
            {/* Include global styles for webkit scrollbars */}
            <Global styles={scrollbarStyles.globalStyles} />
            <ToolOutputDownloadContext.Provider value={toolOutputDownload}>
              <ChatThread
                messages={currentChatHistory}
                isAIBuffering={isAIBuffering}
                isIngestionActive={isIngestionActive}
                isHitlTurnInFlight={isHitlTurnInFlight}
                iterationWarning={iterationWarning}
                hasHitlApproval={Object.keys(hitlApprovals).length > 0}
                announcedExecutionKeys={announcedExecutionKeys}
                onExecutionAnnounced={handleExecutionAnnounced}
                onHitlDecide={(decision: HitlDecision) => {
                  // Never emit the decision while ingestion is active — the
                  // backend rejects resume_conversation in that state, and
                  // this row has no data left to re-show once cleared, so
                  // we must not clear it either.
                  if (isIngestionActive || isHitlTurnInFlight) return;
                  const toolCallId = decision.tool_call_id;
                  // Echo the confirmation_id the server minted for this exact
                  // pending call so it can refuse an answer that doesn't belong
                  // to it. Omitted when the request carried none.
                  const confirmationId = toolCallId
                    ? hitlApprovalsRef.current[toolCallId]?.confirmation_id
                    : undefined;
                  const outgoingDecision: HitlDecision = confirmationId
                    ? { ...decision, confirmation_id: confirmationId }
                    : decision;
                  try {
                    const userToken = userRef.current?.access_token || '';
                    SocketService.sendHitlDecision(outgoingDecision, userToken);
                    setIsHitlTurnInFlight(true);
                    if (toolCallId) {
                      // Snapshot before removing, in case the backend rejects
                      // this send outright (turn_in_flight) and the row needs
                      // to be put back — see pendingHitlSendCacheRef above.
                      const originalRequest =
                        hitlApprovalsRef.current[toolCallId];
                      if (originalRequest) {
                        pendingHitlSendCacheRef.current[toolCallId] =
                          originalRequest;
                      }
                      setHitlApprovals((prev) => {
                        const next = { ...prev };
                        delete next[toolCallId];
                        return next;
                      });
                      const next = { ...hitlApprovalsRef.current };
                      delete next[toolCallId];
                      hitlApprovalsRef.current = next;
                      if (decision.action === 'reject') {
                        // No tool_call_result will ever follow a rejected
                        // call — the backend never invokes the tool — so this
                        // row must be settled locally, not left spinning.
                        markHitlRowSettled(
                          toolCallId,
                          'error',
                          'Rejected by user.',
                          'rejected',
                        );
                      } else if (decision.action === 'approve') {
                        // Back to running; the eventual tool_call_result
                        // finalizes it (success/error), same as any other call.
                        markHitlRowSettled(
                          toolCallId,
                          'running',
                          undefined,
                          'approved',
                        );
                      }
                    }
                  } catch (err) {
                    console.error('[AIModule] sendHitlDecision failed', err);
                  }
                }}
                planApproval={planApproval}
                onPlanApprove={handlePlanApprove}
                onPlanReject={handlePlanReject}
                onPlanRequestChanges={handlePlanRequestChanges}
                onDismissWarning={() => setIterationWarning(null)}
                loopId={loopId || ''}
                selectedLoopId={selectedLoopId || null}
                showWelcome={showWelcome}
                activeTaskInfo={activeTaskInfo}
                availableLoops={availableLoops || []}
                onLoopSelect={handleLoopSelection}
                selectedTaskStarters={selectedTaskStarters}
                darkMode={darkMode}
                userName={getCurrentUserDisplayName()}
                onTaskStarterClick={handleTaskSendToChat}
              />
            </ToolOutputDownloadContext.Provider>
            {integrationIssue && (
              <NoIntegrationAccessNotice reason={integrationIssue} />
            )}
            {selectedLoopId && (
              <ChatInputArea
                darkMode={darkMode}
                noLoopWarning={noLoopWarning}
                showClearCommand={showClearCommand}
                showToolSuggestions={showToolSuggestions}
                isLoadingTools={isLoadingTools}
                toolSuggestions={toolSuggestions}
                allToolSuggestions={allToolSuggestions}
                selectedTool={selectedTool}
                contextWindow={contextWindow}
                displayValue={displayValue}
                inputMessage={inputMessage}
                isListening={isListening}
                isAIBuffering={isAIBuffering}
                isIngestionActive={isIngestionActive}
                isStopping={isStopping}
                hasHitlApproval={Object.keys(hitlApprovals).length > 0}
                planApproval={planApproval}
                setInputMessage={setInputMessage}
                setShowClearCommand={setShowClearCommand}
                setShowToolSuggestions={setShowToolSuggestions}
                setToolInputIndex={setToolInputIndex}
                setToolSuggestions={setToolSuggestions}
                setSelectedTool={setSelectedTool}
                setNoLoopWarning={setNoLoopWarning}
                fetchToolsFromAPI={fetchToolsFromAPI}
                handleSendMessageWithTool={handleSendMessageWithTool}
                handleClearCommand={handleClearCommand}
                handleToolSelection={handleToolSelection}
                handleVoiceInput={handleVoiceInput}
                onAbortAndStop={handleAbortAndStop}
              />
            )}
          </>
        )}
        {activeTab === 'tasks' && selectedLoopId && (
          <TasksTab
            selectedLoopId={selectedLoopId || loopId}
            selectedLoopName={selectedLoopName}
            teamspaceId={teamspaceId ?? ''}
            teamspaceName={teamspaceName ?? ''}
            isBusy={isAIBuffering || isIngestionActive}
            disabledTooltip={
              isIngestionActive ? t('ingestion-task-blocked') : undefined
            }
            onSendToChat={handleTaskSendToChat}
            onTaskSelected={async (
              taskId: string,
              taskName: string,
              starters: TaskStarter[],
              taskIcon?: {
                name?: string;
                emoji?: string;
                color?: string;
                data?: string;
              },
              taskDescription?: string,
            ) => {
              setActiveTab('chats');
              const chatLoopId = selectedLoopId || loopId || '';
              if (!chatLoopId) return;
              // Prevent switching tasks while one is running
              if (isAIBuffering) {
                setNoLoopWarning(
                  t('task-in-progress', {
                    defaultValue: 'Task in progress. Please wait.',
                  }),
                );
                return;
              }

              // Store full task icon object as JSON for sending with messages
              // (includes name, emoji, color, data — needed for MIME inference on history reload)
              lastTaskIconRef.current = taskIcon
                ? JSON.stringify(taskIcon)
                : null;
              lastTaskNameRef.current = taskName || null;

              // Track active task info for the UI header banner
              setActiveTaskInfo({ name: taskName, icon: taskIcon });

              // Clear stale clarification/approval from a previous session so
              // messages typed after selecting the task start a fresh conversation.
              setActiveClarification(null);
              settleAllPendingHitl();
              setPlanApproval(null);
              markPlanDecided('expired');
              setIterationWarning(null);
              SocketService.disconnect();

              // Prepare task intro content
              const subMessage = (
                <div
                  id="id-ai-module-task-quick-starters"
                  data-testid="ai-module-task-quick-starters"
                  aria-label={t('quick-starters')}
                >
                  <div
                    id="id-ai-module-task-starters-info"
                    data-testid="ai-module-task-starters-info"
                    className="font-[Roboto,Arial,sans-serif] text-sm leading-6 text-[#1C262E]"
                  >
                    {`Okay, let's dive into the ${taskName} task. Which of the following steps would you like me to complete. You can also type in a new step below.`}
                  </div>
                  <div
                    id="id-ai-module-task-starters-divider"
                    data-testid="ai-module-task-starters-divider"
                    className="my-2 border-t border-[#D3D9DE]"
                  />
                  <div
                    id="id-ai-module-task-starters-label-wrapper"
                    data-testid="ai-module-task-starters-label-wrapper"
                  >
                    <span
                      id="id-ai-module-task-starters-section-label"
                      data-testid="ai-module-task-starters-section-label"
                      className="font-[Roboto] text-base font-semibold text-[#1C262E]"
                    >
                      {t('task-starters-section-label', { taskName })}:
                    </span>
                  </div>
                  <div
                    id="id-ai-module-task-starters-container"
                    data-testid="ai-module-task-starters-container"
                    className="mt-2 flex flex-wrap gap-2"
                  >
                    <TooltipProvider>
                      {starters.map((s: TaskStarter, idx: number) => (
                        <Tooltip key={`${taskId}-${idx}`}>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (isAIBuffering || isIngestionActive) return;
                                handleTaskSendToChat(taskId, s.prompt);
                              }}
                              disabled={isAIBuffering || isIngestionActive}
                              aria-label={`${t('starter')}: ${s.label}`}
                              className="rounded-[6px] border-[#E1E5E9] bg-[#F5F5FA] font-[Roboto] normal-case text-[#1C262E] shadow-[0px_4px_4px_0px_#00000012] hover:bg-gray-100"
                            >
                              {s.label}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{s.prompt}</TooltipContent>
                        </Tooltip>
                      ))}
                    </TooltipProvider>
                  </div>
                </div>
              );
              // Continuity: keep the SAME session id and existing messages so the
              // backend reuses one thread and the agent retains the prior chat.
              lastTaskIdSentRef.current = taskId;
              // Append a divider with the task intro + starters (do NOT clear).
              // Deliberately not stampTurn()'d: task selection is disabled
              // while a turn is running, so this never falls inside one —
              // it only ever sits between turns, where an unstamped
              // (legacy-grouped) message is harmless.
              appendTaskDivider(chatLoopId, {
                sender: 'ai' as const,
                message: taskName,
                subMessage,
                taskMeta: { taskId, taskName, taskDescription },
              });
            }}
          />
        )}
        {activeTab === 'mcp' && selectedLoopId && (
          <MCPConnectionsTab
            teamspaceId={teamspaceId ?? ''}
            loopId={selectedLoopId}
          />
        )}
        {activeTab === 'history' && selectedLoopId && (
          <HistoryTab
            selectedLoopId={selectedLoopId || loopId}
            selectedLoopName={selectedLoopName}
            teamspaceId={teamspaceId ?? ''}
            teamspaceName={teamspaceName ?? ''}
            isBusy={isAIBuffering}
            onHistoryItemClick={(
              historyId: string,
              title: string,
              messages?: any[],
              taskInfo?: {
                type: 'chat' | 'task';
                taskIcon?: string;
                taskName?: string;
                taskId?: string;
              },
              turns?: BackendTurn[],
            ) => {
              if (
                (messages && messages.length > 0) ||
                (turns && turns.length > 0)
              ) {
                // Merge-by-turn-presence (contracts/history-data-service.md):
                // any turn present in `turns` replaces the plain-text
                // mapper's output for that turn; anything with no
                // turn-scoped record at all falls back to plain text
                // unchanged (FR-012, FR-013). This guard predates `turns`
                // existing at all and was never widened for it — a
                // conversation whose plain-text save failed but whose
                // richer backend record was fetched successfully used to
                // open empty despite `mergeHistoryMessages` being fully
                // capable of rendering from `turns` alone (found in review
                // of #989, confirmed empirically).
                // Each stored-output row remembers the conversation it was restored from, so a
                // later New Chat elsewhere cannot redirect its download.
                const formattedMessages = mergeHistoryMessages(
                  messages ?? [],
                  turns ?? [],
                ).map((msg) =>
                  msg.sender === 'tool' && msg.toolOutputRef
                    ? {
                        ...msg,
                        toolOutputRef: {
                          ...msg.toolOutputRef,
                          conversationId: historyId,
                        },
                      }
                    : msg,
                );
                setLocalChatHistories({
                  ...localChatHistories,
                  [selectedLoopId || loopId || '']: formattedMessages,
                } as typeof localChatHistories);

                // Restore task header banner when loading a task-based history item
                if (taskInfo?.type === 'task') {
                  // Derive task name: prefer stored task_name, then extract
                  // from task_id path (e.g. "TASKS/Build Management.md" → "Build Management"),
                  // then fall back to history item title.
                  let taskName = taskInfo.taskName || '';
                  if (!taskName && taskInfo.taskId) {
                    const path = taskInfo.taskId.replace(/^TASKS\//i, '');
                    taskName = path.replace(/\.md$/i, '');
                  }
                  if (!taskName) taskName = title;

                  // task_icon from backend is stored as JSON string of the full icon object
                  let parsedIcon:
                    | {
                        name?: string;
                        emoji?: string;
                        color?: string;
                        data?: string;
                      }
                    | undefined;
                  if (taskInfo.taskIcon) {
                    try {
                      parsedIcon = JSON.parse(taskInfo.taskIcon);
                    } catch {
                      // Fallback: treat as raw data string (legacy format)
                      parsedIcon = { data: taskInfo.taskIcon };
                    }
                  }
                  setActiveTaskInfo({
                    name: taskName,
                    icon: parsedIcon,
                  });
                  // Restore task-mode refs so subsequent messages include
                  // task_id/task_icon/task_name in the Socket.IO payload.
                  lastTaskIdSentRef.current = taskInfo.taskId || null;
                  lastTaskIconRef.current = taskInfo.taskIcon || null;
                  lastTaskNameRef.current =
                    taskInfo.taskName || taskName || null;
                } else {
                  setActiveTaskInfo(null);
                  lastTaskIdSentRef.current = null;
                  lastTaskIconRef.current = null;
                  lastTaskNameRef.current = null;
                }
              } else {
                setActiveTaskInfo(null);
                lastTaskIdSentRef.current = null;
                lastTaskIconRef.current = null;
                lastTaskNameRef.current = null;
              }
              _chatSessionId = historyId;
              bumpSessionIdVersion((v) => v + 1);
              setActiveTab('chats');
            }}
          />
        )}
      </div>

      {teamspaceId && tenantBaseUrl && (
        <DefaultIntegrationSelectionModal
          open={needsIntegrationChoice}
          teamspaceId={teamspaceId}
          tenantBaseUrl={tenantBaseUrl}
          getUserContext={getIntegrationUserContext}
          onSelected={closeIntegrationChoice}
          onDismiss={closeIntegrationChoice}
        />
      )}
    </div>
  );
};

export default AIModule;
